import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../utils/appLanguages";
import { localizedExerciseName } from "../utils/localizedCatalogFields";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MovieIcon from "@mui/icons-material/Movie";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MessageIcon from "@mui/icons-material/Message";

import { exdetails, hrsummary, autoevolution, sessionexlist } from "./funtionsBD/Mainservice";
import { CoachExerciseEvalRoster } from "./CoachExerciseEvalRoster";
import {
  isKotExercise,
  isOffTableExercise,
  isTeOnce,
  supportsPlayerPerformanceAutoeval,
} from "../utils/teExecutionType";
import { ExerciseToolResults } from "./ExerciseToolResults";
import { redirectToLoginIfSessionExpired } from "../utils/authExpired";
import { assertVideoUploadAllowed } from "../utils/videoUploadNetwork";
import { ip_back } from "../config";
import {
  attachVideoUrlsToKotRows,
  buildKotMatchVideoUrlMap,
  buildKotPlayerMatchRow,
  dedupeFeedbackVideoMessages,
  findFeedbackForKotMatch,
  formatKotPlayerDisplayName,
  parseKotMatchIdFromFeedbackText,
  resolveOpponentNameFromMatch,
  sortKotPlayerMatchRows,
  toAbsoluteMediaUrl,
} from "../utils/kotPlayerMatches";
import { KotPlayerMatchList } from "./KotPlayerMatchList";
import { MobileReviewVideo } from "./MobileReviewVideo";
import { ReviewVideoFullscreenPlayer } from "./ReviewVideoFullscreenPlayer";
import { MoveEvaluationVideoDialog } from "./MoveEvaluationVideoDialog";
import ClipShareSheet from "./social/ClipShareSheet";
import ChangeExerciseOnResultsDialog from "./ChangeExerciseOnResultsDialog";
import {
  needsCatalogExercise,
  resultsExerciseDisplayName,
} from "../utils/resultsExerciseCatalog";
import { BenchmarkVideoDialog } from "./BenchmarkVideoDialog";
import { VideoAnalysisSettingsDialog } from "./VideoAnalysisSettingsDialog";
import {
  EvaluationVideoMediaCard,
  formatEvaluationVideoDescription,
} from "./EvaluationVideoMediaCard";
import {
  buildEvaluationVideoMetadata,
  formatEvaluationVideoCardDate,
  formatExerciseTableLabel,
  formatParticipationRoleLabel,
  getSequenceSubtypeLabel,
  isPersonalMatchEvaluationVideo,
  isMaxRallyEvaluationVideo,
  isStrokeSlowMoEvaluationVideo,
} from "../utils/evaluationVideoMediaMeta";
import {
  formatCameraSetupLabel,
} from "../utils/evaluationCameraSetup";
import "./pose3d/pose3d.css";
import { useKotExerciseFeedbacks } from "../hooks/useKotExerciseFeedbacks";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import {
  useSubjectPhysiology,
  physiologyFromPickerSources,
} from "../utils/playerPhysiology";
import { isPlayerAccount, readActiveHomeRole, isModeratorStaff } from "../utils/homeRole";
import { buildHrSeries, normalizeSubjectRoleKind, sequenceHasHrWindow } from "../utils/hrPerformanceCurve";
import { rebaseHrPointsToClipStart, formatHrOverlayFirstName, hasOrientVideoTransform } from "../utils/videoViewSetup";
import { bakeEvaluationVideoOrients, reencodeEvaluationVideo, formatReencodeEta, reencodeJobProgressPct } from "../api/evaluationVideoBakeApi";
import { prefetchSignedMediaUrls } from "../api/signedMediaUrl";
import { loadVideoAnalysisSettings } from "../utils/videoAnalysisSettings";
import {
  resolveExercisePlayerById,
  resolveViewerTableNumber,
} from "../utils/exerciseViewerTable";
import {
  buildEvaluationVideoCoverage,
  collectSequenceEvaluationVideos,
  dedupeEvaluationVideosByUrl,
  evaluationFeedbackMatchesViewer,
  feedbackSequencePlausibleForExercise,
  feedbackVideoBelongsToTe,
  isManualTeAssignment,
  filterEvaluationVideosForViewer,
  findSequenceForFeedbackMsg,
  formatEvaluationCameraOptionLabel,
  resolveEvaluationVideoCameraOrdinal,
  resolveEvaluationVideoCameraPosition,
  resolveEvaluationVideoCameraSetup,
  resolveFeedbackSequenceOrder,
  resolveRosterOpponentForViewer,
  resolveSequenceRoleForPlayer as resolveSequenceRoleForPlayerUtil,
  resolveSequenceRoleForViewer,
  sortCameraVideoOptions,
  viewerHasActiveOrSparringParticipation,
} from "../utils/evaluationVideoMatching";
import { TePipelineDebugPanel } from "./TePipelineDebugPanel";
import { HrPerformanceCurveChart } from "./HrPerformanceCurveChart";
import { TrainingZonesInfoPanel } from "./TrainingZonesInfoPanel";
import { ExerciseCardHrSummary } from "./ExerciseCardHrSummary";
import { ExerciseCardDefaultMetrics } from "./ExerciseCardDefaultMetrics";
import { fetchDefaultExerciseMetrics } from "../api/defaultExerciseMetricsApi";
import { pickReelActivePlayerId } from "../utils/strokeSlowMoReel";
import { CoachObjectiveMetricPicker } from "./CoachObjectiveMetricPicker";
import { getSocket } from "../socket";
import "./PlayerSessionResultsDialog.css";

const Pose3DSetupDialog = lazy(() => import("./pose3d/Pose3DSetupDialog"));
const Pose3DWebViewer = lazy(() => import("./pose3d/Pose3DWebViewer"));

const formatExerciseStartTime = (timeRaw, localeCode) => {
  if (!timeRaw) return "";
  const isoParsed = moment(timeRaw, moment.ISO_8601, true);
  if (isoParsed.isValid()) return isoParsed.locale(localeCode).format("LT");
  const timeParsed = moment(timeRaw, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  if (timeParsed.isValid()) return timeParsed.locale(localeCode).format("LT");
  return String(timeRaw).slice(0, 5);
};

const formatExerciseDuration = (ex, t) => {
  if (!ex) return "";
  const minLabel = t("min") || "min";
  const durationMinutes = Number(ex.te_duration) || 0;
  if (durationMinutes > 0) {
    return `${durationMinutes} ${minLabel}`;
  }
  const totalDuration = Number(ex.total_duration) || 0;
  if (totalDuration > 0) {
    return `${totalDuration} ${minLabel}`;
  }
  const seqTotal = (ex.sequences || []).reduce(
    (acc, s) => acc + (Number(s.sequence_duration) || 0),
    0
  );
  if (seqTotal > 0) {
    return `${seqTotal} ${minLabel}`;
  }
  return "";
};

function calculateAge(birthDateStr) {
  if (!birthDateStr) return null;
  const birthDate = new Date(birthDateStr);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function getAgeBasedMaxHeartRate(age, gender) {
  const clampedAge = Math.max(5, Math.min(100, age || 30));
  const g = String(gender || "male").trim().toLowerCase();
  let maxHeartRate;
  if (g === "female" || g === "f") {
    maxHeartRate = Math.round(206 - 0.88 * clampedAge);
  } else {
    maxHeartRate = Math.round(208 - 0.7 * clampedAge);
  }
  return Math.max(120, Math.min(200, maxHeartRate));
}

function formatZoneDuration(sec) {
  const s = Math.round(sec);
  if (s <= 0) return "0s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m > 0) {
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  }
  return `${r}s`;
}

const PERF_ICONS = ["😞", "😕", "😐", "🙂", "🤩"];
const EXERTION_ICONS = ["💤", "🚶", "🏃", "🔥", "💥"];

const PERF_LABELS = {
  en: ["Poor", "Below Avg.", "Average", "Good", "Excellent"],
  fr: ["Faible", "Sous moy.", "Moyen", "Bon", "Excellent"],
  de: ["Schlecht", "Unter Avg", "Mittel", "Gut", "Exzellent"],
  es: ["Malo", "Bajo prom.", "Promedio", "Bueno", "Excelente"],
  nl: ["Zwak", "Onder gem.", "Gemiddeld", "Goed", "Uitstekend"],
  pt: ["Fraco", "Abaixo da média", "Médio", "Bom", "Excelente"],
};

const EXERTION_LABELS = {
  en: ["Very light", "Light", "Moderate", "Hard", "Maximum"],
  fr: ["Très léger", "Léger", "Modéré", "Intense", "Maximum"],
  de: ["Sehr leicht", "Leicht", "Mittel", "Schwer", "Maximum"],
  es: ["Muy ligero", "Ligero", "Moderado", "Intenso", "Máximo"],
  nl: ["Zeer licht", "Licht", "Gemiddeld", "Zwaar", "Maximaal"],
  pt: ["Muito leve", "Leve", "Moderado", "Intenso", "Máximo"],
};

const PERF_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"];
const EXERTION_COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];

function EmojiRatingRow({ icons, labels, colors, value, onChange, title }) {
  return (
    <div className="player-exercise-details__emoji-eval-block">
      <h4 className="player-exercise-details__eval-label">{title}</h4>
      <div className="player-exercise-details__emoji-eval-row">
        {icons.map((emoji, idx) => {
          const rating = idx + 1;
          const isSelected = value === rating;
          const color = colors[idx];
          return (
            <button
              key={rating}
              type="button"
              className={`player-exercise-details__emoji-eval-tile${
                isSelected ? " player-exercise-details__emoji-eval-tile--selected" : ""
              }`}
              style={{ "--emoji-tile-color": color }}
              onClick={() => onChange(rating)}
            >
              <span className="player-exercise-details__emoji-eval-icon">{emoji}</span>
              <span className="player-exercise-details__emoji-eval-caption">{labels[idx]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function authHeaders() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function multipartAuthHeaders() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token ? { Authorization: token } : {};
}

function parseRating(val) {
  if (!val) return null;
  const s = String(val).trim();
  const n = parseInt(s, 10);
  if (n >= 1 && n <= 5) return n;
  const parts = s.split("-");
  if (parts.length === 2) {
    const avg = (parseInt(parts[0], 10) + parseInt(parts[1], 10)) / 2;
    return Math.round(Math.min(5, Math.max(1, avg / 2)));
  }
  return null;
}

function isActiveRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  return r === "active" || r.includes("actif") || r.includes("activo") || r.includes("aktiv");
}

/** Roles that should show HR on the review video (active + both). */
function isStrictActiveHrRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r === "both") return true;
  return isActiveRole(role);
}

function isSparringRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  return r === "sparring" || r.includes("sparring") || r.includes("spar");
}

function isActiveOrSparringParticipationRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r === "both") return true;
  return isActiveRole(role) || isSparringRole(role);
}

function resolveSequenceRoleForPlayer(seq, ids) {
  return resolveSequenceRoleForPlayerUtil(seq, ids);
}

function playerMatchesIds(playerLike, ids) {
  if (!playerLike || !ids.length) return false;
  return ids.some(
    (id) =>
      (playerLike.player_id != null && String(playerLike.player_id) === id) ||
      (playerLike.user_id != null && String(playerLike.user_id) === id)
  );
}

function isBothParticipationRole(role) {
  return String(role || "").toLowerCase() === "both";
}

function getSequenceDurationSeconds(seq) {
  const startMs = seq?.tes_start_time
    ? new Date(seq.tes_start_time).getTime()
    : null;
  const endMs = seq?.tes_end_time ? new Date(seq.tes_end_time).getTime() : null;
  if (startMs && endMs && endMs > startMs) {
    return Math.round((endMs - startMs) / 1000);
  }
  const minutes = Number(seq?.sequence_duration);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Math.round(minutes * 60);
}

const SEQUENCE_PLAYER_SLOT_KEYS = [
  { idKey: "player_a_id", roleKey: "player_a_role", playerKey: "player_a" },
  { idKey: "player_b_id", roleKey: "player_b_role", playerKey: "player_b" },
  { idKey: "player_c_id", roleKey: "player_c_role", playerKey: "player_c" },
  { idKey: "player_d_id", roleKey: "player_d_role", playerKey: "player_d" },
];

function buildSequencePlayerSlots(seq) {
  if (!seq) return [];
  return SEQUENCE_PLAYER_SLOT_KEYS.map(({ idKey, roleKey, playerKey }) => ({
    id: seq[idKey] ?? null,
    role: seq[roleKey] ?? null,
    embedded: seq[playerKey] ?? null,
  })).filter((slot) => slot.id || slot.embedded);
}

/**
 * Active / both players on a sequence for review-video HR overlays.
 */
function resolveActiveHrPlayersForSequence(seq, exerciseContext) {
  if (!seq) return [];
  const seen = new Set();
  const out = [];
  for (const slot of buildSequencePlayerSlots(seq)) {
    if (!isStrictActiveHrRole(slot.role)) continue;
    const playerId = slot.id ? String(slot.id) : null;
    if (!playerId || seen.has(playerId)) continue;
    seen.add(playerId);
    const player =
      resolveSequencePlayerRecord(slot, exerciseContext) ||
      resolveExercisePlayerById(exerciseContext, playerId);
    out.push({
      playerId,
      playerName:
        formatHrOverlayFirstName(player, playerId) ||
        formatExercisePlayerName(player, playerId),
      userId: player?.user_id != null ? String(player.user_id) : null,
      role: slot.role,
    });
  }
  return out;
}

function sequenceSlotMatchesViewer(slot, viewerIds) {
  if (!slot || !viewerIds.length) return false;
  if (slot.id && viewerIds.includes(String(slot.id))) return true;
  return slot.embedded ? playerMatchesIds(slot.embedded, viewerIds) : false;
}

function resolveSequencePlayerRecord(slot, exerciseContext) {
  if (!slot) return null;
  if (slot.embedded) return slot.embedded;
  if (slot.id) return resolveExercisePlayerById(exerciseContext, slot.id);
  return null;
}

function extractPlayerRanking(player) {
  if (!player) return { classement: null, rankingPoints: null };
  const classement =
    player.fltt_classement ||
    player.classement ||
    player.classment ||
    null;
  const rawPts = player.fltt_ranking_points ?? player.ranking_points ?? null;
  const rankingPoints =
    rawPts != null && Number.isFinite(Number(rawPts)) ? Number(rawPts) : null;
  return { classement, rankingPoints };
}

function formatPartnerRankingLabel(classement, rankingPoints) {
  const level = classement ? String(classement).trim() : "";
  const pts =
    rankingPoints != null && Number.isFinite(Number(rankingPoints))
      ? Math.round(Number(rankingPoints))
      : null;
  if (level && pts != null) return `${level} · ${pts} pts`;
  if (level) return level;
  if (pts != null) return `${pts} pts`;
  return null;
}

function buildReelOwners(seq, exerciseContext, t) {
  const owners = [];
  const seen = new Set();
  const pushOwner = (player, playerId, role, side = null) => {
    const name = formatExercisePlayerName(player, playerId);
    const key = playerId ? String(playerId) : name;
    if (!key || key === "—" || seen.has(key)) return;
    seen.add(key);
    const ranking = extractPlayerRanking(player);
    const level = ranking.classement ? String(ranking.classement).trim() : "";
    const roleKey = String(role || "")
      .trim()
      .toLowerCase();
    owners.push({
      name,
      playerId: playerId ? String(playerId) : null,
      side: side || null,
      roleKey: roleKey || null,
      role: formatParticipationRoleLabel(role, t),
      level: level || null,
    });
  };

  const slots = buildSequencePlayerSlots(seq);
  slots.forEach((slot, index) => {
    const playerId = slot.id ? String(slot.id) : null;
    const player =
      resolveSequencePlayerRecord(slot, exerciseContext) ||
      (playerId ? resolveExercisePlayerById(exerciseContext, playerId) : null);
    const side = index === 0 ? "A" : index === 1 ? "B" : String(index);
    pushOwner(player, playerId, slot.role, side);
  });

  if (!owners.length && exerciseContext) {
    pushOwner(
      exerciseContext.player1,
      exerciseContext.player1?.player_id,
      seq?.player_a_role || "active",
      "A"
    );
    pushOwner(
      exerciseContext.player2,
      exerciseContext.player2?.player_id,
      seq?.player_b_role || "sparring",
      "B"
    );
  }
  return owners;
}

function resolveSequenceOpponent(seq, viewerIds, exerciseContext) {
  if (!seq) return null;
  const slots = buildSequencePlayerSlots(seq);
  const viewerSlot = slots.find((slot) => sequenceSlotMatchesViewer(slot, viewerIds));

  if (viewerSlot) {
    const opponentSlot = slots.find((slot) => slot !== viewerSlot);
    if (opponentSlot) {
      const player = resolveSequencePlayerRecord(opponentSlot, exerciseContext);
      const ranking = extractPlayerRanking(player);
      return {
        name: formatExercisePlayerName(player, opponentSlot.id),
        role: opponentSlot.role,
        ...ranking,
        rankingLabel: formatPartnerRankingLabel(
          ranking.classement,
          ranking.rankingPoints
        ),
      };
    }
  }

  if (!exerciseContext) return null;

  // Multi-table warmup: partner is the other side on the same physical table.
  const rosterOpponent = resolveRosterOpponentForViewer(
    exerciseContext,
    viewerIds
  );
  if (rosterOpponent) {
    const viewerRow = (exerciseContext.players || []).find((p) =>
      playerMatchesIds(p, viewerIds)
    );
    const viewerPos = String(viewerRow?.player_position || "")
      .replace(/\s+/g, "")
      .toLowerCase();
    const opponentIsSideB =
      viewerPos === "player1" || viewerPos === "a";
    const ranking = extractPlayerRanking(rosterOpponent);
    return {
      name: formatExercisePlayerName(
        rosterOpponent,
        rosterOpponent.player_id
      ),
      role: opponentIsSideB
        ? seq.player_b_role || "sparring"
        : seq.player_a_role || "active",
      ...ranking,
      rankingLabel: formatPartnerRankingLabel(
        ranking.classement,
        ranking.rankingPoints
      ),
    };
  }

  if (playerMatchesIds(exerciseContext.player1, viewerIds)) {
    const opponent = exerciseContext.player2;
    const ranking = extractPlayerRanking(opponent);
    return {
      name: formatExercisePlayerName(opponent, opponent?.player_id),
      role: seq.player_b_role || "sparring",
      ...ranking,
      rankingLabel: formatPartnerRankingLabel(
        ranking.classement,
        ranking.rankingPoints
      ),
    };
  }
  if (playerMatchesIds(exerciseContext.player2, viewerIds)) {
    const opponent = exerciseContext.player1;
    const ranking = extractPlayerRanking(opponent);
    return {
      name: formatExercisePlayerName(opponent, opponent?.player_id),
      role: seq.player_a_role || "active",
      ...ranking,
      rankingLabel: formatPartnerRankingLabel(
        ranking.classement,
        ranking.rankingPoints
      ),
    };
  }

  return null;
}

function resolveParticipationRoleVariant(role) {
  if (!role) return "neutral";
  if (isBothParticipationRole(role) || isActiveRole(role)) return "active";
  if (isSparringRole(role)) return "sparring";
  return "neutral";
}

function resolvePlayerRoleForViewer(seq, viewerIds, exerciseContext) {
  return resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
}

function resolveSequenceSlotIdentityIds(seq, position, exerciseContext) {
  if (!seq || !position) return [];
  const key = String(position).toUpperCase();
  const idKey =
    key === "A"
      ? "player_a_id"
      : key === "B"
        ? "player_b_id"
        : key === "C"
          ? "player_c_id"
          : key === "D"
            ? "player_d_id"
            : null;
  if (!idKey) return [];
  const ids = [];
  const slotId = seq[idKey];
  if (slotId != null) ids.push(String(slotId));
  const player = resolveExercisePlayerById(exerciseContext, slotId);
  if (player?.player_id != null) ids.push(String(player.player_id));
  if (player?.user_id != null) ids.push(String(player.user_id));
  return [...new Set(ids.filter(Boolean))];
}

/** Pose metrics target the active (evaluated) player — left on camera A, right on camera B. */
function viewerIsEvaluatedPlayer(seq, viewerIds, exerciseContext) {
  const role = resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
  if (!role) return false;
  return isActiveRole(role) || isBothParticipationRole(role);
}

function buildSequenceCameraVideoOptions(
  seq,
  videoLists,
  viewerIds,
  sequences,
  exerciseContext,
  t = null
) {
  const messages = collectSequenceEvaluationVideos(
    seq,
    videoLists,
    viewerIds,
    sequences,
    exerciseContext
  );
  if (!messages.length) return [];

  const options = messages.map((msg) => {
    const resolveSlot = (s, position) =>
      resolveSequenceSlotIdentityIds(s, position, exerciseContext);
    const cameraSetup = resolveEvaluationVideoCameraSetup(msg, seq, resolveSlot);
    const cameraOrdinal = resolveEvaluationVideoCameraOrdinal(msg);
    const cameraPosition =
      resolveEvaluationVideoCameraPosition(msg, seq, resolveSlot) ||
      (cameraSetup
        ? String(cameraSetup).endsWith("_b")
          ? "B"
          : "A"
        : null);
    const cameraSetupLabel = formatCameraSetupLabel(
      cameraSetup,
      cameraOrdinal,
      t
    );
    return {
      url: msg.video_message_url,
      cameraPosition,
      cameraSetup,
      cameraOrdinal,
      cameraSetupLabel,
      isLefty: String(msg?.sender_player_hand || "").toLowerCase() === "left",
      messageId: msg.message_id || null,
    };
  });

  return sortCameraVideoOptions(options);
}

function SequencePerformanceCard({
  seq,
  idx,
  durationLabel,
  localeCode,
  t,
  role = null,
  roleLabel,
  opponentName,
  opponentRoleLabel,
  opponentRankingLabel = null,
  roleVariant = "neutral",
  orderLabel,
  videoOptions = null,
  videoUrl = null,
  videoTitle = null,
  hasHr = false,
  defaultMetrics = null,
  showStrokeMetrics = false,
  onPlayVideo = null,
  onOpenHr = null,
  showTable = false,
}) {
  const order = orderLabel ?? seq.sequence_order ?? idx + 1;
  const label =
    getSequenceSubtypeLabel(seq, localeCode) ||
    `${t("sequence", "Sequence")} ${order}`;
  const opponentRoleDisplay = opponentRoleLabel
    ? formatParticipationRoleLabel(opponentRoleLabel, t) || opponentRoleLabel
    : null;
  const opponentRoleVariant = resolveParticipationRoleVariant(opponentRoleLabel);
  const resolvedVideos =
    Array.isArray(videoOptions) && videoOptions.length
      ? videoOptions
      : videoUrl
        ? [{ url: videoUrl, cameraPosition: null, isLefty: false }]
        : [];
  const hasVideo = resolvedVideos.length > 0;
  const showActions = !!(hasVideo || hasHr);
  const showFooter = showActions || !!durationLabel;
  const [cameraMenuOpen, setCameraMenuOpen] = useState(false);
  const [cameraMenuStyle, setCameraMenuStyle] = useState(null);
  const videoActionRef = useRef(null);
  const cameraMenuRef = useRef(null);

  useEffect(() => {
    if (!cameraMenuOpen) {
      setCameraMenuStyle(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const anchor = videoActionRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = Math.min(220, window.innerWidth - 24);
      const estimatedHeight = Math.min(
        resolvedVideos.length * 44 + 16,
        Math.floor(window.innerHeight * 0.45)
      );
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        120,
        Math.min(estimatedHeight, openUp ? spaceAbove : spaceBelow)
      );
      let left = rect.right - menuWidth;
      left = Math.max(12, Math.min(left, window.innerWidth - menuWidth - 12));

      if (openUp) {
        setCameraMenuStyle({
          position: "fixed",
          left,
          bottom: window.innerHeight - rect.top + gap,
          width: menuWidth,
          maxHeight,
          zIndex: 12000,
        });
      } else {
        setCameraMenuStyle({
          position: "fixed",
          left,
          top: rect.bottom + gap,
          width: menuWidth,
          maxHeight,
          zIndex: 12000,
        });
      }
    };

    updateMenuPosition();

    const onPointerDown = (event) => {
      const inAnchor = videoActionRef.current?.contains(event.target);
      const inMenu = cameraMenuRef.current?.contains(event.target);
      if (!inAnchor && !inMenu) setCameraMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setCameraMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    // Capture scroll from nested dialog body so the menu stays aligned.
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [cameraMenuOpen, resolvedVideos.length]);

  const playSingleOrToggleMenu = (e) => {
    e.stopPropagation();
    if (resolvedVideos.length <= 1) {
      const only = resolvedVideos[0];
      if (only?.url) {
        onPlayVideo?.(only.url, videoTitle || label, only.messageId || null);
      }
      setCameraMenuOpen(false);
      return;
    }
    setCameraMenuOpen((open) => !open);
  };

  const ariaRole = roleLabel || roleVariant;
  const cardAriaLabel = ariaRole
    ? `${t("sequence", "Sequence")} ${order}, ${ariaRole}`
    : `${t("sequence", "Sequence")} ${order}`;

  const cameraMenu =
    cameraMenuOpen && resolvedVideos.length > 1 && cameraMenuStyle
      ? createPortal(
          <div
            ref={cameraMenuRef}
            className="player-exercise-details__seq-camera-menu player-exercise-details__seq-camera-menu--portal"
            role="menu"
            aria-label={t("selectSequenceCamera", "Select camera")}
            style={cameraMenuStyle}
          >
            {resolvedVideos.map((option, optionIdx) => {
              const optionLabel = formatEvaluationCameraOptionLabel(
                option,
                t,
                optionIdx
              );
              const badge =
                option.cameraSetupLabel ||
                option.cameraPosition ||
                optionIdx + 1;
              return (
                <button
                  key={option.messageId || option.url || optionIdx}
                  type="button"
                  role="menuitem"
                  className="player-exercise-details__seq-camera-menu-item"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCameraMenuOpen(false);
                    onPlayVideo?.(
                      option.url,
                      `${label} · ${optionLabel}`,
                      option.messageId || null
                    );
                  }}
                >
                  <span
                    className={`player-exercise-details__seq-camera-badge${
                      option.cameraPosition
                        ? ` player-exercise-details__seq-camera-badge--${String(
                            option.cameraPosition
                          ).toLowerCase()}`
                        : ""
                    }`}
                  >
                    {badge}
                  </span>
                  <span className="player-exercise-details__seq-camera-menu-label">
                    {optionLabel}
                    {option.isLefty
                      ? ` · ${t("leftyCameraBadge", "Lefty cam")}`
                      : ""}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className={`player-exercise-details__perf-seq-card player-exercise-details__perf-seq-card--${roleVariant}`}
      aria-label={cardAriaLabel}
    >
      <div className="player-exercise-details__seq-header">
        <div className="player-exercise-details__seq-header-main">
          <div
            className={`player-exercise-details__seq-num player-exercise-details__seq-num--${roleVariant}`}
            aria-hidden="true"
          >
            {order}
          </div>
          <h4 className="player-exercise-details__seq-title">{label}</h4>
          {showTable && Number(seq?.table_number) > 0 ? (
            <span className="player-exercise-details__seq-table">
              {t("table_with_number", {
                number: Number(seq.table_number),
                defaultValue: `Table ${Number(seq.table_number)}`,
              })}
            </span>
          ) : null}
        </div>
        {roleLabel ? (
          <span
            className={`player-exercise-details__seq-role-pill player-exercise-details__seq-role-pill--${roleVariant}`}
          >
            {roleLabel}
          </span>
        ) : null}
      </div>

      {resolvedVideos.length > 0 ? (
        <div
          className="player-exercise-details__seq-camera-sources"
          aria-label={t("cameraSources", "Camera sources")}
        >
          {resolvedVideos.map((option, optionIdx) => {
            const optionLabel = formatEvaluationCameraOptionLabel(
              option,
              t,
              optionIdx
            );
            return (
              <button
                key={option.messageId || option.url || optionIdx}
                type="button"
                className="player-exercise-details__seq-camera-chip"
                title={optionLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  onPlayVideo?.(
                    option.url,
                    `${label} · ${optionLabel}`,
                    option.messageId || null
                  );
                }}
              >
                {optionLabel}
              </button>
            );
          })}
        </div>
      ) : null}

      {opponentName ? (
        <div className="player-exercise-details__seq-partner">
          <div className="player-exercise-details__seq-partner-line">
            <span className="player-exercise-details__seq-opponent-prefix">
              {t("with_partner", "With")}
            </span>
            <span className="player-exercise-details__seq-opponent-name">
              {opponentName}
            </span>
          </div>
          {(opponentRankingLabel || opponentRoleDisplay) ? (
            <div className="player-exercise-details__seq-partner-meta">
              {opponentRankingLabel ? (
                <span className="player-exercise-details__seq-partner-rank">
                  {opponentRankingLabel}
                </span>
              ) : null}
              {opponentRoleDisplay ? (
                <span
                  className={`player-exercise-details__seq-role-pill player-exercise-details__seq-role-pill--sm player-exercise-details__seq-role-pill--${opponentRoleVariant}`}
                >
                  {opponentRoleDisplay}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {showStrokeMetrics ? (
        <ExerciseCardDefaultMetrics
          metrics={defaultMetrics}
          t={t}
          className="player-exercise-details__seq-default-metrics"
        />
      ) : null}

      {showFooter ? (
        <div className="player-exercise-details__seq-footer">
          {showActions ? (
            <div className="player-exercise-details__seq-actions">
              {hasVideo ? (
                <div
                  className="player-exercise-details__seq-video-action"
                  ref={videoActionRef}
                >
                  <button
                    type="button"
                    className={`player-exercise-details__seq-action-btn player-exercise-details__seq-action-btn--video${
                      cameraMenuOpen
                        ? " player-exercise-details__seq-action-btn--video-open"
                        : ""
                    }`}
                    aria-label={
                      resolvedVideos.length > 1
                        ? t("selectSequenceCamera", "Select camera")
                        : t("watchSequenceVideo", "Watch sequence video")
                    }
                    aria-haspopup={resolvedVideos.length > 1 ? "menu" : undefined}
                    aria-expanded={
                      resolvedVideos.length > 1 ? cameraMenuOpen : undefined
                    }
                    onClick={playSingleOrToggleMenu}
                  >
                    <PlayCircleFilledIcon style={{ fontSize: 18 }} aria-hidden />
                    {resolvedVideos.length > 1 ? (
                      <span className="player-exercise-details__seq-video-count">
                        {resolvedVideos.length}
                      </span>
                    ) : null}
                  </button>
                  {cameraMenu}
                </div>
              ) : null}
              {hasHr ? (
                <button
                  type="button"
                  className="player-exercise-details__seq-action-btn player-exercise-details__seq-action-btn--hr"
                  aria-label={t(
                    "viewSequenceHeartRate",
                    "View heart rate for this sequence"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenHr?.(seq, order, role);
                  }}
                >
                  <FavoriteIcon style={{ fontSize: 16 }} aria-hidden />
                </button>
              ) : null}
            </div>
          ) : (
            <div className="player-exercise-details__seq-actions" />
          )}
          {durationLabel ? (
            <div className="player-exercise-details__seq-dur">{durationLabel}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PlayerPerformedSequencesList({
  sequences,
  localeCode,
  t,
  onPlayVideo = null,
  onOpenHr = null,
}) {
  if (!sequences.length) return null;

  const distinctTables = new Set(
    sequences
      .map((entry) => Number(entry?.seq?.table_number))
      .filter((n) => Number.isFinite(n) && n > 0)
  );
  const showTable = distinctTables.size > 1;
  const activeCount = sequences.filter((e) => e.roleVariant === "active").length;
  const sparringCount = sequences.filter((e) => e.roleVariant === "sparring").length;
  const showRoleLegend = activeCount > 0 || sparringCount > 0;

  return (
    <div className="player-exercise-details__perf-seq-list-wrap">
      {showRoleLegend ? (
        <div className="player-exercise-details__perf-role-legend" aria-hidden="true">
          {activeCount > 0 ? (
            <span className="player-exercise-details__perf-role-legend-item player-exercise-details__perf-role-legend-item--active">
              <span className="player-exercise-details__perf-role-legend-dot" />
              {activeCount} {t("performance_role", "Performance")}
            </span>
          ) : null}
          {sparringCount > 0 ? (
            <span className="player-exercise-details__perf-role-legend-item player-exercise-details__perf-role-legend-item--sparring">
              <span className="player-exercise-details__perf-role-legend-dot" />
              {sparringCount} {t("sparring", "Sparring")}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="player-exercise-details__perf-seq-grid">
        {sequences.map(
          ({
            seq,
            idx,
            durationLabel,
            role,
            roleLabel,
            opponentName,
            opponentRoleLabel,
            opponentRankingLabel,
            roleVariant,
            orderLabel,
            videoOptions,
            videoUrl,
            videoTitle,
            hasHr,
            defaultMetrics,
            showStrokeMetrics,
          }) => (
            <SequencePerformanceCard
              key={seq.tes_id || `perf-seq-${idx}`}
              showTable={showTable}
              seq={seq}
              idx={idx}
              durationLabel={durationLabel}
              localeCode={localeCode}
              t={t}
              role={role}
              roleLabel={roleLabel}
              opponentName={opponentName}
              opponentRoleLabel={opponentRoleLabel}
              opponentRankingLabel={opponentRankingLabel}
              roleVariant={roleVariant}
              orderLabel={orderLabel}
              videoOptions={videoOptions}
              videoUrl={videoUrl}
              videoTitle={videoTitle}
              hasHr={hasHr}
              defaultMetrics={defaultMetrics}
              showStrokeMetrics={showStrokeMetrics}
              onPlayVideo={onPlayVideo}
              onOpenHr={onOpenHr}
            />
          )
        )}
      </div>
    </div>
  );
}

function parseFeedbackViewSetup(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function feedbackVideoUrlKey(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  // Ignore signing query churn (X-Amz-*) so OVH polls do not remount Media.
  return raw.split("?")[0].split("#")[0];
}

/** Stable compare so silent feedback polls do not remount Media cards. */
function feedbackListUnchanged(prev, next) {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (String(a?.message_id ?? "") !== String(b?.message_id ?? "")) return false;
    if (
      feedbackVideoUrlKey(a?.video_message_url) !==
      feedbackVideoUrlKey(b?.video_message_url)
    ) {
      return false;
    }
    const aStatus = parseFeedbackViewSetup(a?.view_setup)?.web_playback?.status;
    const bStatus = parseFeedbackViewSetup(b?.view_setup)?.web_playback?.status;
    if (String(aStatus ?? "") !== String(bStatus ?? "")) return false;
  }
  return true;
}

function formatExercisePlayerName(player, playerId) {
  if (player) {
    const alias = player.user_alias || player.player_alias;
    const rosterName = player.player_name || player.name;
    const first = player.user_first_name || player.first_name;
    const last = player.user_last_name || player.last_name;
    const full = [first, last].filter(Boolean).join(" ").trim();
    const looksLikeId = (value) => {
      const s = String(value ?? "").trim();
      return !s || /^(pl|us|te|ts|gr|cl)\d+$/i.test(s);
    };
    if (alias && !looksLikeId(alias)) return String(alias).trim();
    if (rosterName && !looksLikeId(rosterName)) return String(rosterName).trim();
    if (full && !looksLikeId(full)) return full;
  }
  // Never surface raw player ids (pl…) in results UI.
  return "—";
}

function buildExercisePlayerContext(exercise, fullExercise) {
  const merged = { ...(fullExercise || {}), ...(exercise || {}) };
  return {
    ...merged,
    players: exercise?.players?.length ? exercise.players : fullExercise?.players,
    player1: exercise?.player1 ?? fullExercise?.player1,
    player2: exercise?.player2 ?? fullExercise?.player2,
    player_details: exercise?.player_details?.length
      ? exercise.player_details
      : fullExercise?.player_details,
    sequences: exercise?.sequences?.length ? exercise.sequences : fullExercise?.sequences,
  };
}

function collectViewerMatchIds(exerciseContext, resolvedPlayerId, resolvedUserId) {
  const ids = new Set();
  if (resolvedUserId) {
    ids.add(String(resolvedUserId));
    const player = resolveExercisePlayerById(exerciseContext, resolvedUserId);
    if (player?.player_id != null) ids.add(String(player.player_id));
  }
  if (resolvedPlayerId) {
    ids.add(String(resolvedPlayerId));
    const player = resolveExercisePlayerById(exerciseContext, resolvedPlayerId);
    if (player?.user_id != null) ids.add(String(player.user_id));
  }
  return Array.from(ids);
}

function feedbackMatchesViewer(msg, matchIds) {
  if (!matchIds.length) return true;
  const sender = msg.sender_id != null ? String(msg.sender_id) : null;
  const receiver = msg.receiver_id != null ? String(msg.receiver_id) : null;
  return matchIds.some((id) => id === sender || id === receiver);
}


function MediaVideoPlayRow({
  label,
  description,
  date,
  videoUrl,
  playLabel,
  onPlay,
  messageId = null,
}) {
  if (!videoUrl) return null;

  return (
    <div className="player-exercise-details__video-entry">
      <div className="player-exercise-details__video-entry-body">
        {label && (
          <span className="player-exercise-details__video-entry-label">{label}</span>
        )}
        {date && (
          <span className="player-exercise-details__video-entry-date">{date}</span>
        )}
        {description && (
          <p className="player-exercise-details__video-entry-desc">{description}</p>
        )}
      </div>
      <button
        type="button"
        className="player-exercise-details__video-play-btn"
        onClick={() => onPlay(videoUrl, label, messageId)}
        aria-label={playLabel}
      >
        <PlayCircleFilledIcon style={{ fontSize: 24 }} />
      </button>
    </div>
  );
}

function formatFeedbackMessageDate(msg, localeCode) {
  return formatEvaluationVideoCardDate(msg, localeCode);
}

function EvaluationVideoCoverageBanner({ coverage, videoCoverageSummary, displayedEvaluationCount, t }) {
  const totalOnExercise =
    displayedEvaluationCount ??
    videoCoverageSummary?.evaluation_video_count ??
    coverage?.totalVideoCount ??
    0;

  if (!coverage?.expectedCount && totalOnExercise <= 0) {
    return null;
  }

  const expected = coverage?.expectedCount ?? 0;
  const found = coverage?.foundCount ?? 0;

  return (
    <div className="player-exercise-details__video-coverage">
      {expected > 0 && (
        <p>
          {t(
            "evalVideoCoverageSummary",
            "{{found}} of {{expected}} sequence video(s) for this player.",
            { found, expected }
          )}
        </p>
      )}
      {totalOnExercise > 0 && (
        <p className="player-exercise-details__video-coverage-meta">
          {t(
            "evalVideoExerciseTotal",
            "{{count}} evaluation video(s) stored for this exercise.",
            { count: totalOnExercise }
          )}
          {videoCoverageSummary?.sequence_count > 0 && (
            <>
              {" "}
              {t(
                "evalVideoSequenceCoverage",
                "{{withVideo}} of {{total}} sequences have at least one video.",
                {
                  withVideo: videoCoverageSummary.sequences_with_video ?? 0,
                  total: videoCoverageSummary.sequence_count,
                }
              )}
            </>
          )}
        </p>
      )}
      {coverage?.extraVideoCount > 0 && (
        <p className="player-exercise-details__video-coverage-warn">
          {t(
            "evalVideoExtraCount",
            "{{count}} additional video(s) on this exercise are from other players or camera angles.",
            { count: coverage.extraVideoCount }
          )}
        </p>
      )}
    </div>
  );
}

function SequenceEvaluationMediaList({
  coverage,
  exercise,
  fullExercise,
  exName,
  sequences,
  viewerIdentityIds,
  localeCode,
  t,
  openReviewVideo,
  canMoveVideo = false,
  onMoveVideo,
  canBenchmark = false,
  onBenchmark,
  canBakeOrient = false,
  bakeOrientBusy = false,
  bakingMessageId = null,
  onBakeOrient,
  activePlaybackMessageId = null,
  kotMatches = null,
  pose3dSelectEnabled = false,
  pose3dSelectedIds = null,
  onPose3dSelect = null,
  canShareReel = false,
  onShareReel = null,
}) {
  if (!coverage?.rows?.length) return null;

  return (
    <div className="player-exercise-details__seq-video-groups">
      {coverage.rows.map((row) => {
        const subtype = getSequenceSubtypeLabel(row.seq, localeCode);
        const tableNum = Number(row.seq?.table_number);
        const tablesInExercise = new Set(
          (sequences || [])
            .map((seq) => Number(seq?.table_number))
            .filter((n) => Number.isFinite(n) && n > 0)
        );
        const tableSuffix =
          tablesInExercise.size > 1 &&
          Number.isFinite(tableNum) &&
          tableNum > 0
            ? ` · ${t("table_with_number", {
                number: tableNum,
                defaultValue: `Table ${tableNum}`,
              })}`
            : "";
        const heading = subtype
          ? `${t("sequence", "Sequence")} ${row.seqOrder} · ${subtype}${tableSuffix}`
          : `${t("sequence", "Sequence")} ${row.seqOrder}${tableSuffix}`;

        return (
          <section
            key={row.seq?.tes_id || row.seqOrder}
            className="player-exercise-details__seq-video-group"
          >
            <h4 className="player-exercise-details__seq-video-group-title">{heading}</h4>
            {row.hasVideo ? (
              row.videos.map((msg, index) => {
                const absoluteUrl = toAbsoluteMediaUrl(msg.video_message_url, ip_back);
                const formattedDate = formatFeedbackMessageDate(msg, localeCode);
                const videoMeta = buildEvaluationVideoMetadata({
                  msg,
                  exercise,
                  fullExercise,
                  exName,
                  sequences,
                  viewerIdentityIds,
                  localeCode,
                  t,
                  videoIndex: index,
                  kotMatches,
                });

                return (
                  <EvaluationVideoMediaCard
                    key={msg.message_id || `${row.seqOrder}-${index}`}
                    msg={msg}
                    meta={videoMeta}
                    formattedDate={formattedDate}
                    videoUrl={absoluteUrl}
                    playLabel={t("watchEvaluationVideo", "Watch evaluation video")}
                    onPlay={openReviewVideo}
                    canMoveVideo={canMoveVideo}
                    onMoveVideo={onMoveVideo}
                    canBenchmark={canBenchmark}
                    onBenchmark={onBenchmark}
                    canBakeOrient={canBakeOrient}
                    bakeOrientBusy={bakeOrientBusy}
                    bakingMessageId={bakingMessageId}
                    onBakeOrient={onBakeOrient}
                    isActivePlayback={
                      activePlaybackMessageId != null &&
                      String(msg.message_id) === String(activePlaybackMessageId)
                    }
                    pose3dSelectEnabled={pose3dSelectEnabled}
                    pose3dSelectIndex={
                      Array.isArray(pose3dSelectedIds)
                        ? pose3dSelectedIds.findIndex(
                            (id) => String(id) === String(msg.message_id)
                          )
                        : null
                    }
                    onPose3dSelect={onPose3dSelect}
                    canShareReel={canShareReel}
                    onShareReel={onShareReel}
                    t={t}
                  />
                );
              })
            ) : (
              <div className="player-exercise-details__seq-video-missing">
                {t(
                  "noEvaluationVideoForSequence",
                  "No evaluation video uploaded for this sequence yet."
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function getAutoEvalFromRecord(record) {
  if (!record) return null;
  if (record.auto_evaluation) {
    return getAutoEvalFromRecord(record.auto_evaluation);
  }
  const data = record.auto_evaluation_data ?? record;
  if (!data) return null;
  const values = data.dataValues ?? data;
  if (values.exercise_perf != null || values.perceived_exertion != null) {
    return values;
  }
  if (values.evaluation_data) {
    return getAutoEvalFromRecord(values.evaluation_data);
  }
  return values;
}

const EXERCISE_RESULT_TABS = new Set(["perf", "hr", "feedback", "autoeval"]);

function normalizeExerciseResultTab(tab) {
  return EXERCISE_RESULT_TABS.has(tab) ? tab : "perf";
}

function sortSessionExercises(list) {
  return [...(Array.isArray(list) ? list : [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
}

function exerciseNavLabel(ex, t, lang = "en") {
  if (!ex) return "";
  return (
    resultsExerciseDisplayName(ex, lang) ||
    `${t("exercise", "Exercise")} ${ex.te_order || ""}`
  );
}

function localizedCatalogExerciseName(ex, lang = "en") {
  return localizedExerciseName(ex, lang);
}

function effectiveResultsExerciseName(ex, playerId, lang = "en") {
  const pid = playerId != null ? String(playerId) : "";
  const details = ex?.player_ex_override_details;
  const override =
    pid && details
      ? details[pid] || details[String(pid)] || null
      : null;
  return (
    localizedCatalogExerciseName(override, lang) ||
    localizedCatalogExerciseName(ex, lang)
  );
}

export function ExerciseResultDialog({
  open,
  onClose,
  onBack,
  exercise: exerciseProp,
  session,
  sessionExercises = null,
  onNavigateExercise = null,
  playerId,
  viewPlayerId,
  viewUserId,
  localeCode = "en",
  onAutoevalUpdated,
  viewerParticipatedInSession = true,
  initialTab = "perf",
  onExerciseCatalogChanged,
}) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("perf"); // 'perf' or 'hr' or 'feedback' or 'autoeval'
  /** Dual-camera Pose3D selection: up to 2 evaluation video message objects */
  const [pose3dSelection, setPose3dSelection] = useState([]);
  const [pose3dSetupOpen, setPose3dSetupOpen] = useState(false);
  const [pose3dViewerOpen, setPose3dViewerOpen] = useState(false);
  const [lastPose3dJobId, setLastPose3dJobId] = useState(null);
  const [lastPose3dResult, setLastPose3dResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [navExercise, setNavExercise] = useState(null);
  const [sessionExerciseList, setSessionExerciseList] = useState([]);

  useBodyScrollLock(open);

  // Prefer in-dialog navigation override; fall back to the prop from the parent.
  const exercise = navExercise ?? exerciseProp;

  useEffect(() => {
    if (!open) setNavExercise(null);
  }, [open]);

  useEffect(() => {
    setNavExercise(null);
  }, [exerciseProp?.te_id]);

  useEffect(() => {
    if (!open) {
      setSessionExerciseList([]);
      return undefined;
    }
    if (Array.isArray(sessionExercises) && sessionExercises.length) {
      setSessionExerciseList(sortSessionExercises(sessionExercises));
      return undefined;
    }
    const tsId = session?.ts_id || session?.id || session?.session_id;
    if (!tsId) {
      setSessionExerciseList([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await sessionexlist({ ts_id: tsId });
        if (cancelled) return;
        setSessionExerciseList(
          sortSessionExercises(response?.data?.data || [])
        );
      } catch {
        if (!cancelled) setSessionExerciseList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    session?.ts_id,
    session?.id,
    session?.session_id,
    sessionExercises,
  ]);

  const exerciseNav = useMemo(() => {
    const list = sessionExerciseList;
    if (!list.length || !exercise?.te_id) {
      return {
        list,
        index: -1,
        total: list.length,
        previous: null,
        next: null,
      };
    }
    let index = list.findIndex(
      (ex) => String(ex?.te_id) === String(exercise.te_id)
    );
    if (index < 0 && exercise?.te_order != null) {
      index = list.findIndex(
        (ex) => Number(ex?.te_order) === Number(exercise.te_order)
      );
    }
    return {
      list,
      index,
      total: list.length,
      previous: index > 0 ? list[index - 1] : null,
      next:
        index >= 0 && index < list.length - 1 ? list[index + 1] : null,
    };
  }, [sessionExerciseList, exercise?.te_id, exercise?.te_order]);

  // Exercise states
  const [fullExercise, setFullExercise] = useState(null);
  const [hrStats, setHrStats] = useState(null);
  const [hrHighlightRole, setHrHighlightRole] = useState(null); // null = Both (plain curve)
  const [selectedHrSequence, setSelectedHrSequence] = useState(null);
  const [exerciseDefaultMetrics, setExerciseDefaultMetrics] = useState(null);
  const [seqDefaultMetricsByOrder, setSeqDefaultMetricsByOrder] = useState({});

  // KoT states
  const [kotMatches, setKotMatches] = useState([]);
  const [kotLeaderboard, setKotLeaderboard] = useState([]);
  const [kotExerciseMatches, setKotExerciseMatches] = useState([]);
  const [kotLoading, setKotKotLoading] = useState(false);
  const [kotError, setKotError] = useState(null);
  const [kotResolvedPlayerId, setKotResolvedPlayerId] = useState(null);

  // Feedback states
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);
  const [bakeOrientBusy, setBakeOrientBusy] = useState(false);
  const [bakeOrientStatus, setBakeOrientStatus] = useState(null);
  const [evaluationUploadNotice, setEvaluationUploadNotice] = useState(null);
  const [bakingMessageId, setBakingMessageId] = useState(null);
  const [videoCoverageSummary, setVideoCoverageSummary] = useState(null);
  const [reviewVideo, setReviewVideo] = useState(null);
  const [reviewVideoHrSeries, setReviewVideoHrSeries] = useState(null);
  const [reelShareTarget, setReelShareTarget] = useState(null);
  const [lastPlayedMessageId, setLastPlayedMessageId] = useState(null);
  const [moveVideoTarget, setMoveVideoTarget] = useState(null);
  const [moveStatus, setMoveStatus] = useState(null);
  const [benchmarkTarget, setBenchmarkTarget] = useState(null);
  const [videoAnalysisSettingsOpen, setVideoAnalysisSettingsOpen] = useState(false);
  const [videoAnalysisSettings, setVideoAnalysisSettings] = useState(() =>
    loadVideoAnalysisSettings()
  );
  const [changeExerciseOpen, setChangeExerciseOpen] = useState(false);
  const [catalogNameOverride, setCatalogNameOverride] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const reviewFeedbackPoolRef = useRef([]);
  const hasFeedbacksRef = useRef(false);

  useEffect(() => {
    setCatalogNameOverride(null);
  }, [exercise?.te_id]);

  const goToSessionExercise = useCallback(
    (target) => {
      if (!target?.te_id) return;
      if (String(target.te_id) === String(exercise?.te_id)) return;
      if (typeof onNavigateExercise === "function") {
        onNavigateExercise(target);
        return;
      }
      setNavExercise(target);
      setFullExercise(null);
      setHrStats(null);
      setHrHighlightRole(null);
      setSelectedHrSequence(null);
      setKotMatches([]);
      setKotLeaderboard([]);
      setKotExerciseMatches([]);
      setKotError(null);
      hasFeedbacksRef.current = false;
      setFeedbacks([]);
      setReviewVideo(null);
      setReviewVideoHrSeries(null);
      setMoveVideoTarget(null);
      setLastPlayedMessageId(null);
      setVideoCoverageSummary(null);
      setCatalogNameOverride(null);
      setChangeExerciseOpen(false);
    },
    [exercise?.te_id, onNavigateExercise]
  );

  const resolveMediaVideoUrl = useCallback((url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(String(url))) return String(url);
    return toAbsoluteMediaUrl(url, ip_back);
  }, []);

  const togglePose3dSelect = useCallback(
    (msg) => {
      if (!msg?.message_id) return;
      setPose3dSelection((prev) => {
        const id = String(msg.message_id);
        const idx = prev.findIndex((p) => String(p.message_id) === id);
        if (idx >= 0) {
          return prev.filter((_, i) => i !== idx);
        }
        if (prev.length >= 2) {
          return [
            prev[1],
            {
              message_id: msg.message_id,
              videoUrl: resolveMediaVideoUrl(msg.video_message_url),
              viewSetup: msg.view_setup || null,
              msg,
            },
          ];
        }
        return [
          ...prev,
          {
            message_id: msg.message_id,
            videoUrl: resolveMediaVideoUrl(msg.video_message_url),
            viewSetup: msg.view_setup || null,
            msg,
          },
        ];
      });
    },
    [resolveMediaVideoUrl]
  );

  const pose3dSelectedIds = useMemo(
    () => pose3dSelection.map((p) => p.message_id),
    [pose3dSelection]
  );

  const openReviewVideo = useCallback(
    (url, title, messageId = null, options = {}) => {
      const absoluteUrl = resolveMediaVideoUrl(url);
      if (!absoluteUrl) return;

      const pool = reviewFeedbackPoolRef.current;
      let match = null;
      if (Array.isArray(pool) && pool.length) {
        match = pool.find((fb) => {
          if (messageId && String(fb?.message_id) === String(messageId)) {
            return true;
          }
          const fbUrl = resolveMediaVideoUrl(fb?.video_message_url);
          return (
            fbUrl &&
            (fbUrl === absoluteUrl ||
              String(fb?.video_message_url || "").trim() === String(url || "").trim())
          );
        });

        // KoT Perf / Media: resolve by match id when URL forms differ (S3 vs local).
        if (!match && options?.kotMatchId) {
          match = findFeedbackForKotMatch(pool, options.kotMatchId);
        }
        if (!match) {
          const fromText = parseKotMatchIdFromFeedbackText(
            options?.textMessage || ""
          );
          if (fromText) match = findFeedbackForKotMatch(pool, fromText);
        }
      }

      const resolvedMessageId = messageId || match?.message_id || null;
      const playbackUrl =
        resolveMediaVideoUrl(match?.video_message_url) || absoluteUrl;
      const seqList = fullExercise?.sequences || exercise?.sequences || [];
      const matchedSeq = match
        ? findSequenceForFeedbackMsg(match, seqList)
        : null;

      let viewSetup = match?.view_setup ?? options?.viewSetup ?? null;
      if (typeof viewSetup === "string" && viewSetup) {
        try {
          viewSetup = JSON.parse(viewSetup);
        } catch {
          /* keep string for parseVideoViewSetup */
        }
      }

      setReviewVideoHrSeries(null);
      setReviewVideo({
        url: playbackUrl,
        title: title || "",
        messageId: resolvedMessageId,
        viewSetup,
        sequenceTimestamp: Number(match?.sequence_timestamp) || 0,
        feedback: match || null,
        sequence: matchedSeq || null,
      });
      if (resolvedMessageId != null) {
        setLastPlayedMessageId(String(resolvedMessageId));
      }
    },
    [resolveMediaVideoUrl, fullExercise?.sequences, exercise?.sequences]
  );

  const reloadFeedbacks = useCallback(async () => {
    if (!exercise?.te_id) return;
    try {
      const resp = await fetch(`${ip_back}api/mobileAPI/getExerciseFeedback`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ te_id: exercise.te_id }),
      });
        const json = await resp.json().catch(() => ({}));
        if (redirectToLoginIfSessionExpired(resp.status, json)) return;
        if (resp.ok && json.status === "success") {
          const nextFeedbacks = dedupeFeedbackVideoMessages(
            json.data?.feedbacks || [],
            { preferredTeId: exercise.te_id }
          );
          setFeedbacks((prev) =>
            feedbackListUnchanged(prev, nextFeedbacks) ? prev : nextFeedbacks
          );
          setVideoCoverageSummary(json.data?.video_coverage || null);
        }
      } catch (err) {
        console.error("Failed to reload feedbacks:", err);
    }
  }, [exercise?.te_id]);

  const saveReviewVideoViewSetup = useCallback(async ({ messageId, viewSetup }) => {
    const resp = await fetch(
      `${ip_back}api/mobileAPI/saveEvaluationVideoViewSetup`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          message_id: messageId,
          view_setup: viewSetup,
        }),
      }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || json.status !== "success") {
      throw new Error(json.message || "Failed to save video view");
    }
    return json.data;
  }, []);

  const requestReviewVideoReupload = useCallback(async ({ messageId }) => {
    const resp = await fetch(
      `${ip_back}api/mobileAPI/requestEvaluationVideoReupload`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ message_id: messageId }),
      }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || json.status !== "success") {
      throw new Error(json.message || "Failed to request video re-upload");
    }
    setReviewVideo(null);
    await reloadFeedbacks();
    return json.data;
  }, [reloadFeedbacks]);

  const openReelShare = useCallback((msg, url) => {
    const videoUrl =
      url ||
      toAbsoluteMediaUrl(msg?.video_message_url, ip_back) ||
      msg?.video_message_url ||
      null;
    if (!videoUrl) return;
    setReelShareTarget({
      videoUrl,
      kind: isMaxRallyEvaluationVideo(msg)
        ? "max_rally"
        : isStrokeSlowMoEvaluationVideo(msg)
          ? "stroke_slowmo"
          : "max_rally",
    });
  }, []);

  const handleViewSetupSaved = useCallback(
    async (data) => {
      const payload =
        data?.video_message_url || data?.message_id
          ? data
          : data?.data?.video_message_url
            ? data.data
            : data?.result?.video_message_url
              ? data.result
              : data;
      const setup = payload?.view_setup ?? null;
      const nextUrlRaw = payload?.video_message_url || null;
      const nextUrl = nextUrlRaw ? resolveMediaVideoUrl(nextUrlRaw) : null;
      setReviewVideo((prev) =>
        prev
          ? {
              ...prev,
              messageId: payload?.message_id || prev.messageId,
              viewSetup: setup,
              ...(nextUrl ? { url: nextUrl } : {}),
            }
          : prev
      );
      await reloadFeedbacks();
    },
    [reloadFeedbacks, resolveMediaVideoUrl]
  );

  const handleMoveVideoSuccess = useCallback(
    (updated) => {
      const movedTeId = updated?.te_id != null ? String(updated.te_id) : null;
      const currentTeId = exercise?.te_id != null ? String(exercise.te_id) : null;
      const movedAway =
        movedTeId && currentTeId && movedTeId !== currentTeId;
      const movedUrl = String(updated?.video_message_url || "")
        .trim()
        .split("?")[0]
        .split("#")[0];
      let movedUrlKey = movedUrl.toLowerCase();
      try {
        if (/^https?:\/\//i.test(movedUrl)) {
          movedUrlKey = (new URL(movedUrl).pathname || movedUrl)
            .replace(/^\/+/, "")
            .toLowerCase();
        } else {
          movedUrlKey = movedUrl.replace(/^\/+/, "").toLowerCase();
        }
      } catch {
        movedUrlKey = movedUrl.replace(/^\/+/, "").toLowerCase();
      }
      const keepIds = new Set(
        [
          updated?.message_id,
          ...(Array.isArray(updated?.sibling_message_ids)
            ? updated.sibling_message_ids
            : []),
        ]
          .filter(Boolean)
          .map(String)
      );

      // Optimistic Media update: drop source orphans / apply new assignment immediately.
      setFeedbacks((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const withoutOrphans = list.filter((msg) => {
          if (!movedUrlKey || !msg?.video_message_url) return true;
          let url = String(msg.video_message_url).trim().split("?")[0].split("#")[0];
          try {
            if (/^https?:\/\//i.test(url)) {
              url = (new URL(url).pathname || url).replace(/^\/+/, "").toLowerCase();
            } else {
              url = url.replace(/^\/+/, "").toLowerCase();
            }
          } catch {
            url = url.replace(/^\/+/, "").toLowerCase();
          }
          if (url !== movedUrlKey) return true;
          if (keepIds.has(String(msg.message_id))) return true;
          // Same clip URL at source sequence/exercise — remove so Media updates now.
          return false;
        });

        if (movedAway) {
          return withoutOrphans.filter(
            (msg) => !keepIds.has(String(msg.message_id))
          );
        }

        const nextMsg = {
          ...updated,
          message_type: updated?.message_type || "evaluation",
          te_id: movedTeId || currentTeId,
          sequence_number:
            updated?.sequence_number != null
              ? Number(updated.sequence_number)
              : updated?.view_setup?.sequence_number ?? null,
          view_setup: updated?.view_setup ?? null,
          updatedAt: updated?.updatedAt || new Date().toISOString(),
        };

        const idx = withoutOrphans.findIndex(
          (msg) => String(msg.message_id) === String(updated?.message_id)
        );
        if (idx >= 0) {
          const copy = [...withoutOrphans];
          copy[idx] = { ...copy[idx], ...nextMsg };
          return dedupeFeedbackVideoMessages(copy, {
            preferredTeId: currentTeId,
          });
        }
        return dedupeFeedbackVideoMessages([...withoutOrphans, nextMsg], {
          preferredTeId: currentTeId,
        });
      });

      setMoveStatus({
        success: true,
        message: movedAway
          ? t(
              "moveVideoSuccessAway",
              "Video moved to another exercise. It will no longer appear on this Media tab."
            )
          : t("moveVideoSuccess", "Video moved successfully."),
      });
      setRefreshCount((prev) => prev + 1);
      reloadFeedbacks();
      setTimeout(() => setMoveStatus(null), 5000);
    },
    [exercise?.te_id, reloadFeedbacks, t]
  );

  const handleDeleteVideoSuccess = useCallback(() => {
    setMoveStatus({
      success: true,
      message: t("deleteEvaluationVideoSuccess", "Evaluation video deleted."),
    });
    reloadFeedbacks();
    setTimeout(() => setMoveStatus(null), 5000);
  }, [reloadFeedbacks, t]);

  const handleChangeExerciseSuccess = useCallback(
    (data) => {
      const name = localizedExerciseName(data?.exercise, currentAppLanguage(i18n)) || null;
      if (name) setCatalogNameOverride(name);
      const queued = Number(data?.clips_enqueued) || 0;
      setMoveStatus({
        success: true,
        message: queued
          ? t(
              "changeExerciseQueued",
              "Exercise updated. Video analysis queued for {{count}} clip(s).",
              { count: queued }
            )
          : t(
              "changeExerciseSuccess",
              "Exercise updated. Video evaluation is using the new recipe."
            ),
      });
      setRefreshCount((n) => n + 1);
      window.dispatchEvent(
        new CustomEvent("smarttt-exercise-catalog-changed", {
          detail: {
            ...(data || {}),
            ts_id: data?.ts_id || session?.ts_id || session?.id || session?.session_id,
          },
        })
      );
      onExerciseCatalogChanged?.(data);
      setTimeout(() => setMoveStatus(null), 6000);
    },
    [i18n.language, onExerciseCatalogChanged, session?.id, session?.session_id, session?.ts_id, t]
  );

  const sessionTsId = session?.ts_id || session?.id || session?.session_id;

  // Role resolution
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  }, []);
  const homeRole = useMemo(() => readActiveHomeRole(user), [user]);
  const isPlayerUi = useMemo(() => isPlayerAccount(user, { homeRole }), [user, homeRole]);
  const isModeratorStaffUi = useMemo(
    () => isModeratorStaff(user, { homeRole }),
    [user, homeRole]
  );
  const loggedInUserId = useMemo(
    () => (user?.user_id != null ? String(user.user_id) : null),
    [user?.user_id]
  );
  const loggedInPlayerId = useMemo(
    () => (user?.player_id != null ? String(user.player_id) : null),
    [user?.player_id]
  );

  useEffect(() => {
    if (!open || exerciseNav.total < 2) return undefined;
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        event.target?.isContentEditable
      ) {
        return;
      }
      if (event.key === "ArrowLeft" && exerciseNav.previous) {
        event.preventDefault();
        goToSessionExercise(exerciseNav.previous);
      } else if (event.key === "ArrowRight" && exerciseNav.next) {
        event.preventDefault();
        goToSessionExercise(exerciseNav.next);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    exerciseNav.total,
    exerciseNav.previous,
    exerciseNav.next,
    goToSessionExercise,
  ]);

  // Auto-evaluation interactive states
  const [isEditingEval, setIsEditingEval] = useState(false);
  const [editPerf, setEditPerf] = useState(0);
  const [editExertion, setEditExertion] = useState(0);
  const [submittingAutoeval, setSubmittingAutoeval] = useState(false);
  const [localAutoEval, setLocalAutoEval] = useState(null);
  const exerciseLoadKeyRef = useRef(null);

  // Coach evaluation interactive states
  const [coachRating, setCoachRating] = useState(0);
  const [coachComments, setCoachComments] = useState("");
  const [submittingCoachEval, setSubmittingCoachEval] = useState(false);

  // Coach rich feedback Hub states
  const [feedbackTab, setFeedbackTab] = useState("text"); // "text" | "voice" | "video"
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Native audio recorder states
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Video file upload states
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  // Request video evaluation states
  const [requestingVideo, setRequestingVideo] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null); // { success: boolean, message: string }

  // Native recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordTime((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };

      setAudioStream(stream);
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordTime(0);
    } catch (err) {
      console.error("Failed to start recording:", err);
      alert("Could not access microphone.");
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
    }
  };

  const resetVoiceRecording = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setRecordTime(0);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
    }
  };

  const resetVideoFile = () => {
    setVideoFile(null);
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoUrl(null);
  };

  const handleSubmitAutoevaluation = async (e) => {
    e?.preventDefault?.();
    if (!editPerf || !editExertion) {
      alert("Please select both a performance and an exertion level.");
      return;
    }
    setSubmittingAutoeval(true);
    try {
      const response = await autoevolution({
        te_id: exercise.te_id,
        exercise_perf: editPerf,
        perceived_exertion: editExertion,
      });
      if (response?.data?.status === "success") {
        const autoEval = {
          exercise_perf: String(editPerf),
          perceived_exertion: String(editExertion),
        };
        setLocalAutoEval(autoEval);
        setIsEditingEval(false);
        setActiveTab("autoeval");
        onAutoevalUpdated?.({ te_id: exercise.te_id, autoEval });
        setRefreshCount((prev) => prev + 1);
      } else {
        alert(response?.data?.message || "Failed to submit evaluation.");
      }
    } catch (err) {
      console.error("Failed to submit autoevaluation:", err);
      alert(err?.message || "An error occurred while submitting evaluation.");
    } finally {
      setSubmittingAutoeval(false);
    }
  };

  const handleSubmitCoachEvaluation = async (e) => {
    e.preventDefault();
    if (!coachRating) {
      alert("Please select a rating.");
      return;
    }
    setSubmittingCoachEval(true);
    try {
      const tsId = session?.ts_id || session?.id || session?.session_id;
      const resp = await fetch(`${ip_back}api/mobileAPI/saveFeedback`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          te_id: exercise.te_id,
          ts_id: tsId,
          receiver_id: resolvedUserId,
          message_type: "evaluation",
          text_message: `[Rating: ${coachRating}/5] ${coachComments || ""}`,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json.status === "success") {
        setCoachRating(0);
        setCoachComments("");
        setRefreshCount((prev) => prev + 1);
      } else {
        alert(json.message || "Failed to save coach evaluation.");
      }
    } catch (err) {
      console.error("Failed to save coach evaluation:", err);
      alert("An error occurred while saving evaluation.");
    } finally {
      setSubmittingCoachEval(false);
    }
  };

  const handleSubmitFeedbackMessage = async (e) => {
    e.preventDefault();
    setSubmittingFeedback(true);
    try {
      const tsId = session?.ts_id || session?.id || session?.session_id;
      const formData = new FormData();
      formData.append("te_id", exercise.te_id);
      if (tsId) formData.append("ts_id", tsId);
      if (resolvedUserId) formData.append("receiver_id", resolvedUserId);

      if (feedbackTab === "text") {
        if (!feedbackText.trim()) {
          alert("Please enter some comment text.");
          setSubmittingFeedback(false);
          return;
        }
        formData.append("message_type", "text");
        formData.append("text_message", feedbackText.trim());
      } else if (feedbackTab === "voice") {
        if (!audioBlob) {
          alert("Please record an audio message first.");
          setSubmittingFeedback(false);
          return;
        }
        formData.append("message_type", "voice");
        formData.append("audio_file", audioBlob, "voice_message.webm");
        if (feedbackText.trim()) {
          formData.append("text_message", feedbackText.trim());
        }
      } else if (feedbackTab === "video") {
        if (!videoFile) {
          alert("Please select a video file first.");
          setSubmittingFeedback(false);
          return;
        }
        assertVideoUploadAllowed();
        formData.append("message_type", "video");
        formData.append("video_file", videoFile);
        if (feedbackText.trim()) {
          formData.append("text_message", feedbackText.trim());
        }
      }

      const resp = await fetch(`${ip_back}api/mobileAPI/saveFeedback`, {
        method: "POST",
        headers: multipartAuthHeaders(),
        body: formData,
      });

      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json.status === "success") {
        setFeedbackText("");
        resetVoiceRecording();
        resetVideoFile();
        setRefreshCount((prev) => prev + 1);
      } else {
        alert(json.message || "Failed to submit feedback.");
      }
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      alert(
        err?.message || "An error occurred while submitting feedback."
      );
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleRequestVideoEvaluation = async (scope) => {
    setRequestingVideo(true);
    setRequestStatus(null);
    try {
      const tsId = session?.ts_id || session?.id || session?.session_id;
      const bodyPayload = {};
      if (scope === "exercise") {
        bodyPayload.te_id = exercise.te_id;
        // Include ts_id so phones can locate multi-sequence clips without a Core Data lookup race.
        if (tsId) bodyPayload.ts_id = tsId;
      } else {
        bodyPayload.ts_id = tsId;
      }

      const resp = await fetch(`${ip_back}api/TE/requestVideoEvaluation`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(bodyPayload),
      });

      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json.status === "success") {
        setRequestStatus({
          success: true,
          message: scope === "exercise" 
            ? t("videoRequestExerciseSuccess", "Video upload request successfully sent for this exercise!")
            : t("videoRequestSessionSuccess", "Video upload request successfully sent for the entire session!"),
        });
      } else {
        setRequestStatus({
          success: false,
          message: json.message || t("videoRequestFailed", "Failed to request video evaluation."),
        });
      }
    } catch (err) {
      console.error("Failed to request video evaluation:", err);
      setRequestStatus({
        success: false,
        message: t("videoRequestError", "An error occurred while requesting video evaluation."),
      });
    } finally {
      setRequestingVideo(false);
      setTimeout(() => {
        setRequestStatus(null);
      }, 5000);
    }
  };

  const renderRequestVideoPanel = () => {
    if (isPlayerUi) return null;
    return (
      <div className="video-request-panel">
        <div className="video-request-panel__glow"></div>
        <div className="video-request-panel__content">
          <div className="video-request-panel__header-section">
            <h3 className="video-request-panel__title">
              {t("videoEvaluationRequestTitle", "Coach Video Upload Requests")}
            </h3>
            <p className="video-request-panel__subtitle">
              {t("videoEvaluationRequestSubtitle", "Instantly notify the player to record and upload performance videos via mobile push/socket.")}
            </p>
          </div>
          <div className="video-request-panel__actions">
            <button
              type="button"
              disabled={requestingVideo}
              className="video-request-panel__btn video-request-panel__btn--exercise"
              onClick={() => handleRequestVideoEvaluation("exercise")}
            >
              {requestingVideo ? (
                <CircularProgress size={14} style={{ color: "currentColor", marginRight: 6 }} />
              ) : (
                <VideocamIcon style={{ fontSize: 16, marginRight: 6 }} />
              )}
              {t("requestThisExerciseVideo", "Request Video: This Exercise")}
            </button>
            <button
              type="button"
              disabled={requestingVideo}
              className="video-request-panel__btn video-request-panel__btn--session"
              onClick={() => handleRequestVideoEvaluation("session")}
            >
              {requestingVideo ? (
                <CircularProgress size={14} style={{ color: "currentColor", marginRight: 6 }} />
              ) : (
                <MovieIcon style={{ fontSize: 16, marginRight: 6 }} />
              )}
              {t("requestAllSessionVideo", "Request Videos: Full Session")}
            </button>
          </div>
          {requestStatus && (
            <div className={`video-request-panel__status video-request-panel__status--${requestStatus.success ? "success" : "error"}`}>
              <span className="video-request-panel__status-dot"></span>
              <p className="video-request-panel__status-msg">{requestStatus.message}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const effectivePlayerId = viewPlayerId && viewPlayerId !== "all" 
    ? String(viewPlayerId) 
    : playerId && playerId !== "all" 
      ? String(playerId) 
      : null;
  const effectiveUserId = viewUserId && viewUserId !== "all" ? String(viewUserId) : null;

  const isKot = useMemo(() => isKotExercise(exercise), [exercise]);
  const performanceEvalExercise = useMemo(
    () =>
      !isKot &&
      (exercise?.performance_eval === true ||
        fullExercise?.performance_eval === true ||
        supportsPlayerPerformanceAutoeval(exercise) ||
        supportsPlayerPerformanceAutoeval(fullExercise)),
    [exercise, fullExercise, isKot]
  );

  useEffect(() => {
    if (isKot && activeTab === "autoeval") setActiveTab("perf");
  }, [isKot, activeTab]);

  const targetPlayer = useMemo(() => {
    const pid = effectivePlayerId ? String(effectivePlayerId) : null;
    const uid = effectiveUserId ? String(effectiveUserId) : null;
    const selfUserId = uid || (isPlayerUi && loggedInUserId ? loggedInUserId : null);

    const rosterSources = [
      ...(Array.isArray(exercise?.players) ? exercise.players : []),
      ...(Array.isArray(fullExercise?.players) ? fullExercise.players : []),
      ...(Array.isArray(exercise?.player_details) ? exercise.player_details : []),
      ...(Array.isArray(fullExercise?.player_details) ? fullExercise.player_details : []),
    ];

    if (selfUserId && rosterSources.length > 0) {
      const selfFromRoster = rosterSources.find(
        (p) => p?.user_id != null && String(p.user_id) === String(selfUserId)
      );
      if (selfFromRoster?.player_id) return selfFromRoster;
    }

    if (rosterSources.length > 0 && (pid || uid)) {
      const found = rosterSources.find(
        (p) =>
          (pid && String(p.player_id) === pid) ||
          (uid && String(p.user_id) === uid) ||
          (pid && String(p.user_id) === pid)
      );
      if (found) return found;
    }

    if (pid || uid) {
      return {
        player_id: pid || (isPlayerUi ? loggedInPlayerId : null),
        user_id: uid || (isPlayerUi ? loggedInUserId : null),
      };
    }

    if (isPlayerUi && loggedInPlayerId) {
      return {
        player_id: loggedInPlayerId,
        user_id: loggedInUserId,
      };
    }

    if (isKot && rosterSources.length > 0 && !isPlayerUi) {
      return rosterSources[0];
    }

    return null;
  }, [
    exercise,
    fullExercise?.player_details,
    fullExercise?.players,
    effectivePlayerId,
    effectiveUserId,
    isKot,
    isPlayerUi,
    loggedInPlayerId,
    loggedInUserId,
  ]);

  const { feedbacks: kotVideoFeedbacks } = useKotExerciseFeedbacks({
    open: open && isKot,
    teId: exercise?.te_id,
    pollWhen: open && isKot && (activeTab === "perf" || activeTab === "feedback"),
  });

  const feedbacksForKotVideo = useMemo(() => {
    if (!isKot) return feedbacks;
    const byId = new Map();
    // Main feedbacks last so a Media-tab reload (with fresh view_setup) wins.
    for (const fb of [...kotVideoFeedbacks, ...feedbacks]) {
      if (fb?.message_id) byId.set(fb.message_id, fb);
    }
    return Array.from(byId.values());
  }, [isKot, feedbacks, kotVideoFeedbacks]);

  reviewFeedbackPoolRef.current = isKot ? feedbacksForKotVideo : feedbacks;

  const resolvedUserId =
    targetPlayer?.user_id ||
    effectiveUserId ||
    (isPlayerUi ? loggedInUserId : null) ||
    null;
  const resolvedPlayerId =
    targetPlayer?.player_id ||
    effectivePlayerId ||
    (isPlayerUi ? loggedInPlayerId : null) ||
    null;
  const kotPlayerId = resolvedPlayerId || kotResolvedPlayerId;
  const kotUserFallbackId =
    !kotPlayerId && isPlayerUi ? resolvedUserId || loggedInUserId : null;

  const viewingOwnProfile =
    !!(
      loggedInUserId &&
      resolvedUserId &&
      String(resolvedUserId) === String(loggedInUserId)
    ) ||
    !!(
      isPlayerUi &&
      loggedInPlayerId &&
      resolvedPlayerId &&
      String(resolvedPlayerId) === String(loggedInPlayerId)
    );

  const inlinePlayerPhysiology = useMemo(() => {
    const sources = [
      targetPlayer,
      ...(Array.isArray(fullExercise?.player_details) ? fullExercise.player_details : []),
      ...(Array.isArray(exercise?.player_details) ? exercise.player_details : []),
      ...(Array.isArray(fullExercise?.players) ? fullExercise.players : []),
      ...(Array.isArray(exercise?.players) ? exercise.players : []),
    ].filter(Boolean);

    const uid = resolvedUserId ? String(resolvedUserId) : null;
    const pid = resolvedPlayerId ? String(resolvedPlayerId) : null;
    const match = sources.find(
      (p) =>
        (pid && String(p.player_id) === pid) ||
        (uid && String(p.user_id) === uid) ||
        (pid && String(p.user_id) === pid)
    );
    if (!match) return { physiology: null, hasDob: false };
    return physiologyFromPickerSources({ groupPlayer: match });
  }, [
    targetPlayer,
    fullExercise?.player_details,
    fullExercise?.players,
    exercise?.player_details,
    exercise?.players,
    resolvedUserId,
    resolvedPlayerId,
  ]);

  const { physiology } = useSubjectPhysiology({
    viewUserId: viewingOwnProfile ? null : resolvedUserId,
    viewPlayerId: viewingOwnProfile ? null : resolvedPlayerId,
    useStorageFallback:
      viewingOwnProfile || (!resolvedUserId && !resolvedPlayerId),
    inlinePhysiology: inlinePlayerPhysiology.physiology,
    inlineHasDob: inlinePlayerPhysiology.hasDob,
  });

  const exercisePlayerContext = useMemo(
    () => buildExercisePlayerContext(exercise, fullExercise),
    [exercise, fullExercise]
  );

  const coachFeedbacks = useMemo(() => {
    if (!feedbacks) return [];
    if (!effectivePlayerId && !effectiveUserId) {
      return feedbacks;
    }
    const matchIds = collectViewerMatchIds(
      exercisePlayerContext,
      resolvedPlayerId,
      resolvedUserId
    );
    const seqList = [...(fullExercise?.sequences || exercise?.sequences || [])].sort(
      (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
    );

    return feedbacks.filter((msg) => {
      if (msg.message_type === "evaluation" && msg.video_message_url) {
        if (
          evaluationFeedbackMatchesViewer(msg, matchIds, seqList, {
            exerciseContext: exercisePlayerContext,
          })
        ) {
          return true;
        }
        // Mobile uploads: also show under the uploading player's Media filter.
        const senderId = String(
          msg.sender_id ?? msg.sender?.user_id ?? ""
        ).trim();
        if (senderId && matchIds.includes(senderId)) return true;

        // Exercise-level / missing-sequence mobile clips: still show for participants.
        if (resolveFeedbackSequenceOrder(msg) == null) {
          return viewerHasActiveOrSparringParticipation(
            seqList,
            matchIds,
            exercisePlayerContext
          );
        }
        return false;
      }
      return feedbackMatchesViewer(msg, matchIds);
    });
  }, [
    feedbacks,
    resolvedUserId,
    resolvedPlayerId,
    effectivePlayerId,
    effectiveUserId,
    exercisePlayerContext,
    fullExercise?.sequences,
    exercise?.sequences,
  ]);

  const coachFeedbackOnly = useMemo(() => coachFeedbacks, [coachFeedbacks]);

  const systemEvaluations = useMemo(() => {
    return coachFeedbacks.filter((msg) => msg.message_type === "evaluation");
  }, [coachFeedbacks]);

  useEffect(() => {
    if (!open) {
      exerciseLoadKeyRef.current = null;
      hasFeedbacksRef.current = false;
      return;
    }
    setActiveTab(normalizeExerciseResultTab(initialTab));
  }, [open, initialTab]);

  // Fetch standard sequence and heart rate data.
  // Wipe the sheet only when the exercise (te / kot) changes — not when player
  // ids fill in or swap formats, which was flickering Media and killing the player.
  useEffect(() => {
    if (!open || !exercise?.te_id) return undefined;

    const loadKey = `${exercise.te_id}:${resolvedPlayerId || ""}:${resolvedUserId || ""}:${isKot ? "kot" : "std"}`;
    const isNewExerciseContext = exerciseLoadKeyRef.current !== loadKey;
    let identityFilledInOnly = false;
    if (isNewExerciseContext) {
      const prevLoadKey = exerciseLoadKeyRef.current;
      exerciseLoadKeyRef.current = loadKey;
      // Closing / wiping when identity only fills in ("" → playerId after roster
      // load) made the sheet flicker and the coach clip vanish after open.
      const prev = String(prevLoadKey || "");
      const [, prevPlayer = "", prevUser = "", prevKind = ""] = prev.split(":");
      const nextPlayer = String(resolvedPlayerId || "");
      const nextUser = String(resolvedUserId || "");
      const nextKind = isKot ? "kot" : "std";
      const prevTe = prev.split(":")[0] || "";
      const teChanged = Boolean(prev) && prevTe !== String(exercise.te_id || "");
      const kindChanged = Boolean(prev) && prevKind !== nextKind;
      const playerSwitched =
        Boolean(prev) &&
        ((prevPlayer && nextPlayer && prevPlayer !== nextPlayer) ||
          (prevUser && nextUser && prevUser !== nextUser));
      const identityFilledIn =
        Boolean(prev) &&
        !teChanged &&
        !kindChanged &&
        ((!prevPlayer && nextPlayer) || (!prevUser && nextUser)) &&
        (!prevPlayer || prevPlayer === nextPlayer) &&
        (!prevUser || prevUser === nextUser);
      identityFilledInOnly = identityFilledIn && !playerSwitched;
      if (!identityFilledInOnly) {
        hasFeedbacksRef.current = false;
        setLoading(true);
        setError(null);
        setFullExercise(null);
        setHrStats(null);
        setExerciseDefaultMetrics(null);
        setSeqDefaultMetricsByOrder({});
        setLocalAutoEval(null);
        setSelectedHrSequence(null);
        setHrHighlightRole(null); // Both: plain red curve + max HR
        setKotResolvedPlayerId(null);
        setIsEditingEval(false);
        setLastPlayedMessageId(null);
      }
      if (!prev || teChanged || kindChanged || (playerSwitched && !identityFilledIn)) {
        setReviewVideo(null);
        setReviewVideoHrSeries(null);
      }
    }

    let cancelled = false;

    async function loadStandardData() {
      try {
        if (exercise.te_id && !isKot) {
          try {
            const detailRes = await exdetails({ te_id: exercise.te_id });
            if (cancelled) return;
            if (detailRes?.data?.data) {
              setFullExercise(detailRes.data.data);
            }
          } catch (detailErr) {
            if (cancelled) return;
            console.warn("Exercise catalog details unavailable:", detailErr);
          }
        } else if (exercise.ex_id && !isKot) {
          try {
            const detailRes = await exdetails({ ex_id: exercise.ex_id });
            if (cancelled) return;
            if (detailRes?.data?.data) {
              setFullExercise(detailRes.data.data);
            }
          } catch (detailErr) {
            if (cancelled) return;
            console.warn("Exercise catalog details unavailable:", detailErr);
          }
        }

        const summaryParams = { te_id: exercise.te_id };
        if (resolvedPlayerId) {
          summaryParams.player_id = resolvedPlayerId;
        } else if (resolvedUserId) {
          summaryParams.user_id = resolvedUserId;
        }

        if (summaryParams.player_id || summaryParams.user_id) {
          try {
            const hrRes = await hrsummary(summaryParams);
            if (cancelled) return;
            if (hrRes?.data?.status === "success") {
              setHrStats(hrRes.data.data || hrRes.data);
            } else {
              setHrStats(null);
            }
          } catch (hrErr) {
            if (cancelled) return;
            setHrStats(null);
            if (!hrErr?.hr_rejection_reason) {
              console.warn("Heart rate summary unavailable:", hrErr);
            }
          }
        } else {
          setHrStats(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load exercise results:", err);
        if (isNewExerciseContext) {
          setError(err?.message || "Failed to load exercise results.");
        }
      } finally {
        // Always clear loading on the latest in-flight request. If identity
        // (player/user id) arrives after the initial open, the first run is
        // cancelled and isNewExerciseContext is false on the retry — requiring
        // that flag here left the Perf tab spinner stuck forever.
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadStandardData();
    return () => {
      cancelled = true;
    };
  }, [open, exercise?.te_id, exercise?.ex_id, resolvedPlayerId, resolvedUserId, isKot, refreshCount]);

  // Fetch KoT match history
  const loadKotMatches = useCallback(async ({ silent = false } = {}) => {
    const tsId = session?.ts_id || session?.id || session?.session_id;
    if (!open || !exercise?.te_id || !tsId || !isKot) return;
    // Players need an identity; coaches/admins can load exercise-wide matches for Media.
    if (!kotPlayerId && !kotUserFallbackId && isPlayerUi) return;
    if (!silent) {
      setKotKotLoading(true);
      setKotError(null);
    }
    try {
      const payload = {
        te_id: exercise.te_id,
        ts_id: tsId,
      };
      if (kotPlayerId) {
        payload.player_id = kotPlayerId;
      } else if (kotUserFallbackId) {
        payload.user_id = kotUserFallbackId;
      }

      const resp = await fetch(`${ip_back}api/kot/playerMatches`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.status !== "success") {
        throw new Error(json.message || "Failed to load matches");
      }
      if (json.data?.player_id) {
        setKotResolvedPlayerId(String(json.data.player_id));
      }
      setKotMatches(Array.isArray(json.data?.matches) ? json.data.matches : []);
      setKotLeaderboard(
        Array.isArray(json.data?.leaderboard) ? json.data.leaderboard : []
      );
      setKotExerciseMatches(
        Array.isArray(json.data?.exercise_matches) ? json.data.exercise_matches : []
      );
    } catch (e) {
      if (!silent) {
        setKotMatches([]);
        setKotLeaderboard([]);
        setKotExerciseMatches([]);
        setKotError(e?.message || String(e));
      }
    } finally {
      if (!silent) setKotKotLoading(false);
    }
  }, [open, exercise?.te_id, session, kotPlayerId, kotUserFallbackId, isKot, isPlayerUi]);

  useEffect(() => {
    if (!isKot) return undefined;
    loadKotMatches({ silent: false });
    let pollId;
    if (open && (activeTab === "perf" || activeTab === "feedback")) {
      pollId = setInterval(() => loadKotMatches({ silent: true }), 12_000);
    }
    return () => {
      if (pollId) clearInterval(pollId);
    };
  }, [isKot, loadKotMatches, open, activeTab]);

  // Sign OVH clip URLs once while the list is open, so playback does not wait.
  useEffect(() => {
    if (!open) return undefined;
    const urls = [];
    for (const msg of feedbacksForKotVideo || []) {
      const abs =
        toAbsoluteMediaUrl(msg?.video_message_url, ip_back) ||
        msg?.video_message_url;
      if (abs) urls.push(abs);
    }
    prefetchSignedMediaUrls(urls);
    return undefined;
  }, [open, feedbacksForKotVideo, ip_back]);

  // Fetch feedback and evaluation videos
  useEffect(() => {
    if (!open || !exercise?.te_id) return;

    async function loadFeedback({ silent = false } = {}) {
      // Avoid Media spinner flicker when we already have clips (tab switch / remux).
      if (!silent && !hasFeedbacksRef.current) setFeedbacksLoading(true);
      try {
        const resp = await fetch(`${ip_back}api/mobileAPI/getExerciseFeedback`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ te_id: exercise.te_id }),
        });
        const json = await resp.json().catch(() => ({}));
        if (redirectToLoginIfSessionExpired(resp.status, json)) return;
        if (resp.ok && json.status === "success") {
          const nextFeedbacks = dedupeFeedbackVideoMessages(
            json.data?.feedbacks || [],
            { preferredTeId: exercise.te_id }
          );
          // Skip no-op polls so iOS remux / OVH signing does not flicker Media.
          setFeedbacks((prev) => {
            const next = feedbackListUnchanged(prev, nextFeedbacks)
              ? prev
              : nextFeedbacks;
            hasFeedbacksRef.current = Array.isArray(next) && next.length > 0;
            return next;
          });
          setVideoCoverageSummary((prev) => {
            const next = json.data?.video_coverage || null;
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
          });
        } else if (!silent) {
          hasFeedbacksRef.current = false;
          setFeedbacks([]);
          setVideoCoverageSummary(null);
        }
      } catch (err) {
        console.error("Failed to load feedbacks:", err);
        if (!silent) {
          hasFeedbacksRef.current = false;
          setFeedbacks([]);
        }
      } finally {
        if (!silent) setFeedbacksLoading(false);
      }
    }

    loadFeedback();

    let pollId;
    if (activeTab === "feedback" || activeTab === "autoeval") {
      pollId = setInterval(() => loadFeedback({ silent: true }), 12_000);
    }
    return () => {
      if (pollId) clearInterval(pollId);
    };
  }, [open, exercise?.te_id, activeTab, refreshCount]);

  // Live refresh when a player finishes uploading an evaluation video.
  useEffect(() => {
    if (!open || !exercise?.te_id) return undefined;
    const socket = getSocket();
    if (!socket) return undefined;
    const teId = String(exercise.te_id);
    let reloadTimer = null;

    const handleUploaded = async (payload) => {
      if (!payload) return;
      if (payload.te_id != null && String(payload.te_id) !== teId) return;
      const url = payload.video_message_url
        ? resolveMediaVideoUrl(payload.video_message_url)
        : null;
      // Debounce remux / dual socket events (evaluationVideoUploaded + newMessage).
      if (reloadTimer) window.clearTimeout(reloadTimer);
      reloadTimer = window.setTimeout(() => {
        reloadFeedbacks().catch(() => {});
      }, 450);
      setActiveTab("feedback");
      setEvaluationUploadNotice({
        message: t(
          "evaluationVideoUploadedNotice",
          "Evaluation video uploaded — tap Watch to play."
        ),
        url,
        messageId: payload.message_id || null,
        title: t("evaluationVideo", "Evaluation video"),
      });
      // If the coach already has this clip open, refresh URL + web_playback
      // (remux finished) instead of leaving "Converting…" stuck forever.
      setReviewVideo((prev) => {
        if (!prev) return prev;
        const payloadMid =
          payload.message_id != null ? String(payload.message_id) : null;
        if (payloadMid && prev.messageId && payloadMid !== String(prev.messageId)) {
          return prev;
        }
        let nextSetup = prev.viewSetup;
        if (payload.view_setup != null) {
          nextSetup = payload.view_setup;
        } else if (payload.web_playback && typeof payload.web_playback === "object") {
          const base =
            nextSetup && typeof nextSetup === "object" && !Array.isArray(nextSetup)
              ? { ...nextSetup }
              : {};
          nextSetup = { ...base, web_playback: payload.web_playback };
        }
        if (typeof nextSetup === "string" && nextSetup) {
          try {
            nextSetup = JSON.parse(nextSetup);
          } catch {
            /* keep string */
          }
        }
        const prevUrlKey = feedbackVideoUrlKey(prev.url);
        const nextUrlKey = feedbackVideoUrlKey(url);
        const urlReallyChanged =
          Boolean(url) && nextUrlKey && nextUrlKey !== prevUrlKey;
        return {
          ...prev,
          messageId: payload.message_id || prev.messageId,
          ...(urlReallyChanged ? { url } : {}),
          ...(nextSetup != null ? { viewSetup: nextSetup } : {}),
        };
      });
    };

    const onEvaluationUploaded = (payload) => {
      handleUploaded(payload).catch(() => {});
    };

    const onNewMessage = (msg) => {
      if (!msg?.video_message_url) return;
      const type = String(msg.message_type || "").toLowerCase();
      if (type !== "evaluation" && type !== "video") return;
      if (msg.te_id != null && String(msg.te_id) !== teId) return;
      handleUploaded(msg).catch(() => {});
    };

    socket.on("evaluationVideoUploaded", onEvaluationUploaded);
    socket.on("newMessage", onNewMessage);
    return () => {
      if (reloadTimer) window.clearTimeout(reloadTimer);
      socket.off("evaluationVideoUploaded", onEvaluationUploaded);
      socket.off("newMessage", onNewMessage);
    };
  }, [open, exercise?.te_id, reloadFeedbacks, resolveMediaVideoUrl, t]);

  // Keep an open player in sync when silent feedback polls land a remux-ready clip.
  useEffect(() => {
    if (!reviewVideo?.messageId || !Array.isArray(feedbacks) || !feedbacks.length) {
      return;
    }
    const mid = String(reviewVideo.messageId);
    const match = feedbacks.find((f) => String(f?.message_id) === mid);
    if (!match) return;
    const setup = parseFeedbackViewSetup(match.view_setup);
    const nextUrl = match.video_message_url
      ? resolveMediaVideoUrl(match.video_message_url)
      : null;
    const prevSetup = parseFeedbackViewSetup(reviewVideo.viewSetup);
    const prevStatus = prevSetup?.web_playback?.status ?? null;
    const nextStatus = setup?.web_playback?.status ?? null;
    const urlReallyChanged = Boolean(
      nextUrl &&
        feedbackVideoUrlKey(nextUrl) !== feedbackVideoUrlKey(reviewVideo.url)
    );
    const statusChanged = Boolean(nextStatus && nextStatus !== prevStatus);
    if (!urlReallyChanged && !statusChanged) return;
    setReviewVideo((prev) =>
      prev && String(prev.messageId) === mid
        ? {
            ...prev,
            ...(urlReallyChanged ? { url: nextUrl } : {}),
            ...(setup != null ? { viewSetup: setup } : {}),
          }
        : prev
    );
  }, [feedbacks, reviewVideo?.messageId, reviewVideo?.url, reviewVideo?.viewSetup, resolveMediaVideoUrl]);

  const resultsLang = String(i18n.language || localeCode || "en").substring(0, 2);
  const overrideCatalogName = effectiveResultsExerciseName(
    exercise,
    resolvedPlayerId,
    resultsLang
  );
  const exName =
    catalogNameOverride ||
    overrideCatalogName ||
    resultsExerciseDisplayName(exercise, resultsLang) ||
    (exercise ? `${t("exercise", "Exercise")} ${exercise.te_order || 1}` : "");
  const showSetCatalogExercise =
    !isPlayerUi && !catalogNameOverride && needsCatalogExercise(exercise);

  // Sequences list to show (prefers fetched detailed ones, fallback to basic ones)
  const sequences = useMemo(() => {
    const fromExercise = Array.isArray(exercise?.sequences)
      ? exercise.sequences
      : [];
    const fromFull = Array.isArray(fullExercise?.sequences)
      ? fullExercise.sequences
      : [];
    // Prefer session list sequences (getTrainingSessionExercise). getExerciseDetails
    // does not return sequences — an empty [] on fullExercise must not win.
    const rawList = fromExercise.length ? fromExercise : fromFull;
    return [...rawList].sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0));
  }, [fullExercise, exercise]);

  const viewerIdentityIds = useMemo(
    () =>
      [resolvedPlayerId, resolvedUserId, effectivePlayerId, effectiveUserId]
        .filter(Boolean)
        .map(String),
    [resolvedPlayerId, resolvedUserId, effectivePlayerId, effectiveUserId]
  );

  const playerPerformedSequencesBase = useMemo(() => {
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const entries = [];

    sequences.forEach((seq, idx) => {
      const role = resolvePlayerRoleForViewer(seq, viewerIdentityIds, exerciseContext);
      if (viewerIdentityIds.length && !role) return;

      const opponent = resolveSequenceOpponent(seq, viewerIdentityIds, exerciseContext);
      const durationSec = getSequenceDurationSeconds(seq);
      const durationLabel = durationSec > 0 ? formatZoneDuration(durationSec) : null;

      entries.push({
        seq,
        idx,
        durationLabel,
        role,
        roleLabel: formatParticipationRoleLabel(role, t),
        roleVariant: resolveParticipationRoleVariant(role),
        isEvaluatedLeft: viewerIsEvaluatedPlayer(
          seq,
          viewerIdentityIds,
          exerciseContext
        ),
        opponentName: opponent?.name || null,
        opponentRoleLabel: opponent?.role || null,
        opponentRankingLabel: opponent?.rankingLabel || null,
      });
    });

    return entries;
  }, [sequences, viewerIdentityIds, fullExercise, exercise, t]);

  // Default exercise metrics (total_stroke_count, max_rally) for Perf actif sequences + HR tab
  useEffect(() => {
    if (!open || !exercise?.te_id) {
      setExerciseDefaultMetrics(null);
      setSeqDefaultMetricsByOrder({});
      return undefined;
    }

    let cancelled = false;

    async function loadDefaultMetrics() {
      try {
        const exerciseMetrics = await fetchDefaultExerciseMetrics(exercise.te_id, {
          userId: resolvedUserId || undefined,
          playerId: resolvedPlayerId || undefined,
        });
        if (!cancelled) setExerciseDefaultMetrics(exerciseMetrics);

        const leftOrders = (playerPerformedSequencesBase || [])
          .filter((e) => e.isEvaluatedLeft)
          .map((e) => e.orderLabel ?? e.seq?.sequence_order)
          .filter((n) => n != null && Number(n) > 0);

        const byOrder = {};
        await Promise.all(
          leftOrders.map(async (order) => {
            try {
              const m = await fetchDefaultExerciseMetrics(exercise.te_id, {
                sequenceOrder: Number(order),
                userId: resolvedUserId || undefined,
                playerId: resolvedPlayerId || undefined,
              });
              if (
                m &&
                (m.available ||
                  m.total_stroke_count != null ||
                  m.max_rally != null ||
                  m.strokes_per_minute != null ||
                  m.combo_iterations_per_minute != null ||
                  m.combo_max_rally != null)
              ) {
                byOrder[Number(order)] = m;
              }
            } catch {
              /* ignore */
            }
          })
        );
        if (!cancelled) setSeqDefaultMetricsByOrder(byOrder);
      } catch {
        if (!cancelled) {
          setExerciseDefaultMetrics(null);
          setSeqDefaultMetricsByOrder({});
        }
      }
    }

    loadDefaultMetrics();
    return () => {
      cancelled = true;
    };
  }, [open, exercise?.te_id, playerPerformedSequencesBase, resolvedUserId, resolvedPlayerId, refreshCount]);

  const isOnceExercise = useMemo(
    () => isTeOnce(buildExercisePlayerContext(exercise, fullExercise)),
    [exercise, fullExercise]
  );

  const isOffTable = useMemo(
    () =>
      isOffTableExercise(exercise) ||
      isOffTableExercise(buildExercisePlayerContext(exercise, fullExercise)),
    [exercise, fullExercise]
  );

  const teOncePerformanceEntryBase = useMemo(() => {
    if (!isOnceExercise) return null;
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const durationMinutes = Number(exerciseContext.te_duration) || 0;
    const durationSec =
      durationMinutes > 0
        ? Math.round(durationMinutes * 60)
        : getSequenceDurationSeconds(exerciseContext);
    const durationLabel = durationSec > 0 ? formatZoneDuration(durationSec) : null;

    let role = null;
    if (playerMatchesIds(exerciseContext.player1, viewerIdentityIds)) {
      role = "active";
    } else if (playerMatchesIds(exerciseContext.player2, viewerIdentityIds)) {
      role = "sparring";
    }

    const opponent = resolveSequenceOpponent(
      {
        player_a_role: "active",
        player_b_role: "sparring",
      },
      viewerIdentityIds,
      exerciseContext
    );

    return {
      seq: {
        tes_id: `once-${exerciseContext.te_id || "exercise"}`,
        sequence_order: 1,
        sub_type_name_en: exName,
      },
      idx: 0,
      orderLabel: 1,
      durationLabel,
      role,
      roleLabel: formatParticipationRoleLabel(role, t),
      roleVariant: resolveParticipationRoleVariant(role),
      isEvaluatedLeft: playerMatchesIds(
        exerciseContext.player1,
        viewerIdentityIds
      ),
      opponentName: opponent?.name || null,
      opponentRoleLabel: opponent?.role || null,
      opponentRankingLabel: opponent?.rankingLabel || null,
    };
  }, [
    isOnceExercise,
    exercise,
    fullExercise,
    viewerIdentityIds,
    exName,
    t,
  ]);

  const handlePlayKotMatchVideo = useCallback(
    (url, meta = {}) => {
      const title =
        meta?.title ||
        t("watchMatchVideo", "Watch match");
      openReviewVideo(url, title, meta?.messageId || null, {
        kotMatchId: meta?.kotMatchId || null,
        viewSetup: meta?.viewSetup ?? null,
      });
    },
    [openReviewVideo, t]
  );

  const handlePlaySequenceVideo = useCallback(
    (url, title, messageId = null) => {
      openReviewVideo(url, title, messageId);
    },
    [openReviewVideo]
  );

  const handleOpenSequenceHr = useCallback((seq, orderLabel, roleHint = null) => {
    const roleKind =
      normalizeSubjectRoleKind(roleHint) ||
      normalizeSubjectRoleKind(
        resolvePlayerRoleForViewer(
          seq,
          viewerIdentityIds,
          buildExercisePlayerContext(exercise, fullExercise)
        )
      ) ||
      "active";
    const rawTesId = seq?.tes_id != null ? String(seq.tes_id) : null;
    const tesId =
      rawTesId && !rawTesId.startsWith("once-") ? rawTesId : null;
    setHrHighlightRole(roleKind);
    setSelectedHrSequence({
      tesId,
      sequenceOrder:
        orderLabel != null && Number.isFinite(Number(orderLabel))
          ? Number(orderLabel)
          : seq?.sequence_order != null && Number.isFinite(Number(seq.sequence_order))
            ? Number(seq.sequence_order)
            : null,
      role: roleKind,
    });
    setActiveTab("hr");
  }, [viewerIdentityIds, exercise, fullExercise]);

  const handleClearSelectedHrSequence = useCallback(() => {
    setSelectedHrSequence(null);
  }, []);

  const handleHrHighlightRoleChange = useCallback((role) => {
    setSelectedHrSequence(null);
    setHrHighlightRole(role);
  }, []);

  const playerHasActiveOrSparringRole = useMemo(() => {
    if (!viewerIdentityIds.length) return false;
    if (isKot) return true;

    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);

    for (const seq of sequences) {
      const role = resolvePlayerRoleForViewer(
        seq,
        viewerIdentityIds,
        exerciseContext
      );
      if (role && isActiveOrSparringParticipationRole(role)) {
        return true;
      }
    }

    if (!sequences.length) {
      return true;
    }

    const hasSequenceAssignments = sequences.some(
      (seq) =>
        seq?.player_a_id || seq?.player_b_id || seq?.player_c_id || seq?.player_d_id
    );

    if (hasSequenceAssignments) {
      // Multi-table warmup: sequence ids may be stale; roster membership counts.
      const onRoster = (exerciseContext.players || []).some((p) =>
        playerMatchesIds(p, viewerIdentityIds)
      );
      if (onRoster) return true;
      return false;
    }

    const p1 = exerciseContext?.player1;
    const p2 = exerciseContext?.player2;
    return [p1, p2].some((slot) => playerMatchesIds(slot, viewerIdentityIds));
  }, [sequences, viewerIdentityIds, isKot, fullExercise, exercise]);

  const playerEvaluationVideos = useMemo(() => {
    if (!viewerIdentityIds.length) return [];
    return filterEvaluationVideosForViewer(
      feedbacks,
      viewerIdentityIds,
      sequences,
      {
        preferredTeId: exercise?.te_id,
        exerciseContext: exercisePlayerContext,
      }
    );
  }, [
    feedbacks,
    viewerIdentityIds,
    sequences,
    exercise?.te_id,
    exercisePlayerContext,
  ]);

  const coachMediaVideos = useMemo(() => {
    if (!effectivePlayerId && !effectiveUserId) return coachFeedbackOnly;
    return coachFeedbackOnly.filter((msg) => {
      const isEvaluation = msg.message_type === "evaluation";
      const hasVideo =
        !!msg.video_message_url ||
        isEvaluation ||
        msg.message_type === "video" ||
        (!msg.message_type && msg.video_message_url);
      return hasVideo;
    });
  }, [coachFeedbackOnly, effectivePlayerId, effectiveUserId]);

  const coachEvaluationVideoIndexByMessageId = useMemo(() => {
    const indexById = new Map();
    let evaluationIndex = 0;
    for (const msg of coachMediaVideos) {
      if (msg.message_type !== "evaluation" || !msg.video_message_url) continue;
      const key = msg.message_id || `eval-${evaluationIndex}`;
      indexById.set(key, evaluationIndex);
      evaluationIndex += 1;
    }
    return indexById;
  }, [coachMediaVideos]);

  const coachEvaluationVideos = useMemo(
    () =>
      dedupeEvaluationVideosByUrl(
        coachMediaVideos.filter((msg) => {
          const isEvaluation =
            msg.message_type === "evaluation" && !!msg.video_message_url;
          if (!isEvaluation) {
            return false;
          }
          if (
            exercise?.te_id &&
            !feedbackVideoBelongsToTe(msg, exercise.te_id)
          ) {
            return false;
          }
          // Keep orphan / UUID-key uploads and coach-moved clips visible even
          // when the sequence number belongs to another station.
          if (
            isEvaluation &&
            !msg.assignment_orphan &&
            !isManualTeAssignment(msg) &&
            !feedbackSequencePlausibleForExercise(msg, sequences)
          ) {
            return false;
          }
          return true;
        }),
        { preferredTeId: exercise?.te_id }
      ),
    [coachMediaVideos, exercise?.te_id, sequences]
  );

  const orientedEvaluationCount = useMemo(
    () =>
      coachEvaluationVideos.filter((msg) =>
        hasOrientVideoTransform(msg?.view_setup)
      ).length,
    [coachEvaluationVideos]
  );

  const bakeSingleEvaluationVideo = useCallback(
    async (msg) => {
      const messageId = msg?.message_id;
      if (!messageId || bakeOrientBusy || bakingMessageId) return;
      if (!hasOrientVideoTransform(msg?.view_setup)) {
        setBakeOrientStatus({
          success: false,
          message: t(
            "bakeOrientsNoneOne",
            "This video has no rotation/flip to bake."
          ),
        });
        return;
      }
      if (
        !window.confirm(
          t(
            "bakeOrientsConfirmOne",
            "Re-encode this video with its rotation/flip baked into the file?"
          )
        )
      ) {
        return;
      }
      setBakingMessageId(messageId);
      setBakeOrientBusy(true);
      setBakeOrientStatus({
        success: true,
        message: t("bakeOrientsStarting", "Starting re-encode…"),
      });
      try {
        await reencodeEvaluationVideo(messageId, {}, {
          onProgress: (job) => {
            const pct = reencodeJobProgressPct(job);
            const eta = formatReencodeEta(job?.eta_seconds);
            setBakeOrientStatus({
              success: true,
              message: eta
                ? t(
                    "bakeOrientsProgressOneEta",
                    "Re-encoding video… {{pct}}% · ~{{eta}} left",
                    { pct, eta }
                  )
                : t("bakeOrientsProgressOne", "Re-encoding video… {{pct}}%", {
                    pct,
                  }),
            });
          },
        });
        setBakeOrientStatus({
          success: true,
          message: t("bakeOrientsDoneOne", "Video re-encoded. Source URL updated."),
        });
        await reloadFeedbacks();
      } catch (err) {
        setBakeOrientStatus({
          success: false,
          message: err?.message || t("bakeOrientsFailed", "Re-encode failed"),
        });
      } finally {
        setBakingMessageId(null);
        setBakeOrientBusy(false);
      }
    },
    [bakeOrientBusy, bakingMessageId, reloadFeedbacks, t]
  );

  const bakeExerciseVideoOrients = useCallback(async () => {
    const teId = exercise?.te_id || fullExercise?.te_id;
    if (!teId || bakeOrientBusy) return;
    if (
      !window.confirm(
        t(
          "bakeOrientsConfirmExercise",
          "Re-encode all evaluation videos on this exercise that have rotation or flip? The corrected view becomes the new source file."
        )
      )
    ) {
      return;
    }
    setBakeOrientBusy(true);
    setBakeOrientStatus({
      success: true,
      message: t("bakeOrientsStarting", "Starting re-encode…"),
    });
    try {
      const result = await bakeEvaluationVideoOrients({
        teId,
        onProgress: (job) => {
          const done = Number(job?.done) || 0;
          const total = Number(job?.total) || orientedEvaluationCount;
          const pct = reencodeJobProgressPct(job);
          const eta = formatReencodeEta(job?.eta_seconds);
          setBakeOrientStatus({
            success: true,
            message: eta
              ? t(
                  "bakeOrientsProgressEta",
                  "Re-encoding… {{pct}}% ({{done}}/{{total}}) · ~{{eta}} left",
                  { pct, done, total, eta }
                )
              : t(
                  "bakeOrientsProgress",
                  "Re-encoding… {{pct}}% ({{done}}/{{total}})",
                  { pct, done, total }
                ),
          });
        },
      });
      const succeeded = Number(result?.succeeded) || 0;
      const failed = Number(result?.failed) || 0;
      const total = Number(result?.total) || 0;
      if (total === 0) {
        setBakeOrientStatus({
          success: true,
          message: t(
            "bakeOrientsNone",
            "No videos with rotation/flip to re-encode."
          ),
        });
      } else {
        setBakeOrientStatus({
          success: failed === 0,
          message: t(
            "bakeOrientsDone",
            "Re-encode finished: {{ok}} ok, {{fail}} failed.",
            { ok: succeeded, fail: failed }
          ),
        });
        await reloadFeedbacks();
      }
    } catch (err) {
      setBakeOrientStatus({
        success: false,
        message: err?.message || t("bakeOrientsFailed", "Re-encode failed"),
      });
    } finally {
      setBakeOrientBusy(false);
    }
  }, [
    exercise?.te_id,
    fullExercise?.te_id,
    bakeOrientBusy,
    orientedEvaluationCount,
    reloadFeedbacks,
    t,
  ]);

  const coachNonEvaluationMedia = useMemo(
    () =>
      coachMediaVideos.filter(
        (msg) => msg.message_type !== "evaluation" || !msg.video_message_url
      ),
    [coachMediaVideos]
  );

  const evaluationVideoCoverage = useMemo(() => {
    if (!sequences.length || !viewerIdentityIds.length) return null;
    return buildEvaluationVideoCoverage(
      sequences,
      coachEvaluationVideos,
      viewerIdentityIds,
      {
        preferredTeId: exercise?.te_id,
        exerciseContext: exercisePlayerContext,
      }
    );
  }, [
    sequences,
    coachEvaluationVideos,
    viewerIdentityIds,
    exercise?.te_id,
    exercisePlayerContext,
  ]);

  // Sequence coverage only lists clips that match the selected player.
  // A service clip moved onto this exercise from another station must still
  // appear for the moderator when that player filter does not match it.
  const coachClipsMissingFromCoverage = useMemo(() => {
    if (!coachEvaluationVideos.length) return [];
    const grouped =
      evaluationVideoCoverage?.expectedCount > 0 &&
      evaluationVideoCoverage.foundCount > 0;
    if (!grouped) return [];
    const shown = new Set();
    for (const row of evaluationVideoCoverage.rows || []) {
      for (const msg of row.videos || []) {
        if (msg?.message_id != null) shown.add(String(msg.message_id));
      }
    }
    return coachEvaluationVideos.filter(
      (msg) => msg?.message_id == null || !shown.has(String(msg.message_id))
    );
  }, [coachEvaluationVideos, evaluationVideoCoverage]);

  const playerEvaluationVideoCoverage = useMemo(() => {
    if (!sequences.length || !viewerIdentityIds.length) return null;
    return buildEvaluationVideoCoverage(
      sequences,
      playerEvaluationVideos,
      viewerIdentityIds,
      {
        preferredTeId: exercise?.te_id,
        exerciseContext: exercisePlayerContext,
      }
    );
  }, [
    sequences,
    playerEvaluationVideos,
    viewerIdentityIds,
    exercise?.te_id,
    exercisePlayerContext,
  ]);

  const playerPerformedSequences = useMemo(() => {
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const videoLists = [playerEvaluationVideos, coachEvaluationVideos];
    return playerPerformedSequencesBase.map((entry) => {
      const videoOptions = buildSequenceCameraVideoOptions(
        entry.seq,
        videoLists,
        viewerIdentityIds,
        sequences,
        exerciseContext,
        t
      );
      const order = Number(entry.orderLabel ?? entry.seq?.sequence_order);
      const seqMetrics =
        Number.isFinite(order) && seqDefaultMetricsByOrder[order]
          ? seqDefaultMetricsByOrder[order]
          : null;
      return {
        ...entry,
        videoOptions,
        videoUrl: videoOptions[0]?.url || null,
        videoTitle: null,
        hasHr: sequenceHasHrWindow(entry.seq, hrStats),
        defaultMetrics: seqMetrics,
        showStrokeMetrics: Boolean(entry.isEvaluatedLeft),
      };
    });
  }, [
    playerPerformedSequencesBase,
    playerEvaluationVideos,
    coachEvaluationVideos,
    viewerIdentityIds,
    sequences,
    exercise,
    fullExercise,
    hrStats,
    exerciseDefaultMetrics,
    seqDefaultMetricsByOrder,
    t,
  ]);

  const teOncePerformanceEntry = useMemo(() => {
    if (!teOncePerformanceEntryBase) return null;
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const videoOptions = buildSequenceCameraVideoOptions(
      teOncePerformanceEntryBase.seq,
      [playerEvaluationVideos, coachEvaluationVideos],
      viewerIdentityIds,
      sequences,
      exerciseContext,
      t
    );
    return {
      ...teOncePerformanceEntryBase,
      videoOptions,
      videoUrl: videoOptions[0]?.url || null,
      videoTitle: null,
      hasHr: sequenceHasHrWindow(teOncePerformanceEntryBase.seq, hrStats),
      defaultMetrics: teOncePerformanceEntryBase.isEvaluatedLeft
        ? exerciseDefaultMetrics
        : null,
      showStrokeMetrics: Boolean(teOncePerformanceEntryBase.isEvaluatedLeft),
    };
  }, [
    teOncePerformanceEntryBase,
    playerEvaluationVideos,
    coachEvaluationVideos,
    viewerIdentityIds,
    sequences,
    exercise,
    fullExercise,
    hrStats,
    exerciseDefaultMetrics,
  ]);

  // Self Evaluation ratings
  const autoEvalFromPlayer = useMemo(() => {
    return getAutoEvalFromRecord(targetPlayer);
  }, [targetPlayer]);

  const autoEvalFromFullExercise = useMemo(() => {
    if (!Array.isArray(fullExercise?.player_details)) return null;
    const uid = resolvedUserId ? String(resolvedUserId) : null;
    const pid = resolvedPlayerId ? String(resolvedPlayerId) : null;
    const found = fullExercise.player_details.find(
      (p) =>
        (pid && String(p.player_id) === pid) ||
        (uid && String(p.user_id) === uid) ||
        (pid && String(p.user_id) === pid)
    );
    return getAutoEvalFromRecord(found);
  }, [fullExercise?.player_details, resolvedPlayerId, resolvedUserId]);

  const autoEvalFromExerciseDetails = useMemo(
    () => getAutoEvalFromRecord(fullExercise?.auto_evaluation),
    [fullExercise?.auto_evaluation]
  );

  const autoEvalFromExerciseProp = useMemo(
    () =>
      getAutoEvalFromRecord(exercise?.autoEval) ||
      getAutoEvalFromRecord(exercise?.auto_evaluation),
    [exercise?.autoEval, exercise?.auto_evaluation]
  );

  const autoEval =
    localAutoEval ||
    autoEvalFromExerciseProp ||
    autoEvalFromExerciseDetails ||
    autoEvalFromFullExercise ||
    autoEvalFromPlayer;
  const perfVal = parseRating(autoEval?.exercise_perf);
  const exertionVal = parseRating(autoEval?.perceived_exertion);
  const perfLabels = PERF_LABELS[localeCode] || PERF_LABELS.en;
  const exertionLabels = EXERTION_LABELS[localeCode] || EXERTION_LABELS.en;

  useEffect(() => {
    if (!isEditingEval) {
      setEditPerf(perfVal || 0);
      setEditExertion(exertionVal || 0);
    }
  }, [perfVal, exertionVal, isEditingEval]);

  const hasHrData = useMemo(() => {
    if (!hrStats) return false;
    return !!(
      hrStats.maxHR ||
      hrStats.averageHR ||
      hrStats.minHR ||
      (Array.isArray(hrStats.readings) && hrStats.readings.length > 0) ||
      (Array.isArray(hrStats.hrGraph) && hrStats.hrGraph.length > 0)
    );
  }, [hrStats]);

  const showHrTab = !loading && hasHrData;

  useEffect(() => {
    if (!showHrTab && activeTab === "hr") {
      setActiveTab("perf");
    }
  }, [showHrTab, activeTab]);

  // Dynamic HR calculations
  const hrPointsData = useMemo(() => {
    return buildHrSeries(hrStats);
  }, [hrStats]);

  // Load per-active-player HR for the open review clip (avoids mixing both players).
  useEffect(() => {
    if (!reviewVideo?.url || !exercise?.te_id) {
      setReviewVideoHrSeries(null);
      return undefined;
    }

    let cancelled = false;
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const seqList = fullExercise?.sequences || exercise?.sequences || [];
    const seq =
      reviewVideo.sequence ||
      (reviewVideo.feedback
        ? findSequenceForFeedbackMsg(reviewVideo.feedback, seqList)
        : null);

    let actives = resolveActiveHrPlayersForSequence(seq, exerciseContext);

    // TE_ONCE / no sequence: prefer the dialog viewer, else exercise player1/player2 actives.
    if (!actives.length) {
      if (resolvedPlayerId) {
        const player = resolveExercisePlayerById(
          exerciseContext,
          resolvedPlayerId
        );
        actives = [
          {
            playerId: String(resolvedPlayerId),
            playerName:
              formatHrOverlayFirstName(player, resolvedPlayerId) ||
              formatExercisePlayerName(player, resolvedPlayerId),
            userId: player?.user_id != null ? String(player.user_id) : null,
            role: "active",
          },
        ];
      } else {
        const fallbackSlots = [
          {
            id: exerciseContext?.player1?.player_id || null,
            role: "active",
            embedded: exerciseContext?.player1 || null,
          },
          {
            id: exerciseContext?.player2?.player_id || null,
            role: "active",
            embedded: exerciseContext?.player2 || null,
          },
        ].filter((s) => s.id || s.embedded);
        actives = fallbackSlots
          .map((slot) => {
            const playerId = slot.id ? String(slot.id) : null;
            if (!playerId) return null;
            const player =
              resolveSequencePlayerRecord(slot, exerciseContext) ||
              resolveExercisePlayerById(exerciseContext, playerId);
            return {
              playerId,
              playerName:
                formatHrOverlayFirstName(player, playerId) ||
                formatExercisePlayerName(player, playerId),
              userId: player?.user_id != null ? String(player.user_id) : null,
              role: "active",
            };
          })
          .filter(Boolean);
      }
    }

    (async () => {
      const results = await Promise.all(
        actives.map(async (player) => {
          try {
            const params = { te_id: exercise.te_id };
            if (player.playerId) params.player_id = player.playerId;
            else if (player.userId) params.user_id = player.userId;
            // Prefer sequence-scoped series so playhead maps 1:1 to clip time.
            if (seq?.tes_id) params.tes_id = seq.tes_id;

            const hrRes = await hrsummary(params);
            const payload =
              hrRes?.data?.status === "success"
                ? hrRes?.data?.data || hrRes?.data
                : null;
            let points = rebaseHrPointsToClipStart(
              buildHrSeries(payload)?.points
            );
            // If sequence filter returned too few samples, fall back to full exercise.
            if ((!points || points.length < 3) && seq?.tes_id) {
              const fullParams = { ...params };
              delete fullParams.tes_id;
              const fullRes = await hrsummary(fullParams);
              const fullPayload =
                fullRes?.data?.status === "success"
                  ? fullRes?.data?.data || fullRes?.data
                  : null;
              const fullPoints = rebaseHrPointsToClipStart(
                buildHrSeries(fullPayload)?.points
              );
              if (fullPoints?.length > (points?.length || 0)) {
                points = fullPoints;
              }
            }
            if (!points?.length) return null;
            return {
              playerId: player.playerId,
              playerName: player.playerName || null,
              points,
            };
          } catch (err) {
            if (!err?.hr_rejection_reason) {
              console.warn(
                "[reviewVideo HR] failed for player",
                player.playerId,
                err
              );
            }
            return null;
          }
        })
      );

      if (cancelled) return;
      const series = results.filter(Boolean);
      if (series.length) {
        setReviewVideoHrSeries(series);
        return;
      }

      // Fallback: dialog viewer's already-loaded HR tab series.
      const fallback = rebaseHrPointsToClipStart(hrPointsData?.points);
      setReviewVideoHrSeries(
        fallback
          ? [
              {
                playerId: resolvedPlayerId || null,
                playerName: (() => {
                  const player = resolveExercisePlayerById(
                    exerciseContext,
                    resolvedPlayerId
                  );
                  return (
                    formatHrOverlayFirstName(player, resolvedPlayerId) || null
                  );
                })(),
                points: fallback,
              },
            ]
          : null
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [
    reviewVideo?.url,
    reviewVideo?.messageId,
    reviewVideo?.sequence,
    reviewVideo?.feedback,
    exercise,
    fullExercise,
    resolvedPlayerId,
    hrPointsData,
  ]);

  const reviewVideoStrokeReelContext = useMemo(() => {
    if (!reviewVideo?.url) {
      return { activePlayerId: null, sequenceMeta: null, reelOwners: [] };
    }
    const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
    const seqList = fullExercise?.sequences || exercise?.sequences || [];
    const seq =
      reviewVideo.sequence ||
      (reviewVideo.feedback
        ? findSequenceForFeedbackMsg(reviewVideo.feedback, seqList)
        : null);
    let actives = resolveActiveHrPlayersForSequence(seq, exerciseContext);
    if (!actives.length && resolvedPlayerId) {
      actives = [{ playerId: String(resolvedPlayerId) }];
    }
    if (!actives.length && exerciseContext?.player1?.player_id) {
      actives = [{ playerId: String(exerciseContext.player1.player_id) }];
    }
    return {
      activePlayerId: pickReelActivePlayerId(actives, resolvedPlayerId),
      sequenceMeta: seq || null,
      reelOwners: buildReelOwners(seq, exerciseContext, t),
    };
  }, [
    reviewVideo?.url,
    reviewVideo?.sequence,
    reviewVideo?.feedback,
    exercise,
    fullExercise,
    resolvedPlayerId,
    t,
  ]);

  const reviewVideoKotContext = useMemo(() => {
    if (!reviewVideo?.url) {
      return { isKotMatch: false, kotMatchId: null };
    }
    const fromFeedback = parseKotMatchIdFromFeedbackText(
      reviewVideo?.feedback?.text_message || ""
    );
    let fromSetup = null;
    const rawSetup = reviewVideo?.viewSetup;
    if (rawSetup && typeof rawSetup === "object" && rawSetup.kot_match_id) {
      fromSetup = String(rawSetup.kot_match_id);
    } else if (typeof rawSetup === "string" && rawSetup.trim()) {
      try {
        const parsed = JSON.parse(rawSetup);
        if (parsed?.kot_match_id) fromSetup = String(parsed.kot_match_id);
      } catch {
        /* ignore */
      }
    }
    const kotMatchId = fromFeedback || fromSetup || null;
    return {
      isKotMatch: Boolean(isKot || kotMatchId),
      kotMatchId,
    };
  }, [
    reviewVideo?.url,
    reviewVideo?.feedback?.text_message,
    reviewVideo?.viewSetup,
    isKot,
  ]);

  const reviewIsEvalVideo = useMemo(
    () =>
      String(reviewVideo?.feedback?.message_type || "").toLowerCase() ===
        "evaluation" &&
      !reviewVideoKotContext.isKotMatch &&
      !isPersonalMatchEvaluationVideo(reviewVideo?.feedback) &&
      !isStrokeSlowMoEvaluationVideo(reviewVideo?.feedback) &&
      !isMaxRallyEvaluationVideo(reviewVideo?.feedback),
    [reviewVideo?.feedback, reviewVideoKotContext.isKotMatch]
  );
  const reviewIsEvalOrKot =
    reviewVideoKotContext.isKotMatch || reviewIsEvalVideo;
  const reviewStaffTools = reviewIsEvalOrKot
    ? isModeratorStaffUi
    : !isPlayerUi;

  const playerDemographics = useMemo(
    () => ({
      age: physiology?.age ?? 30,
      gender: physiology?.gender || "male",
    }),
    [physiology?.age, physiology?.gender]
  );

  const mhr = useMemo(() => {
    const ageBasedMhr = getAgeBasedMaxHeartRate(playerDemographics.age, playerDemographics.gender);
    return Math.max(120, ageBasedMhr);
  }, [playerDemographics]);

  const { z5Min, z5Max, z4Min, z4Max, z3Min, z3Max, z2Min, z2Max } = useMemo(() => {
    const z5MinVal = Math.floor(mhr * 0.90);
    const z5MaxVal = Math.round(mhr);
    const z4MinVal = Math.floor(mhr * 0.80);
    const z4MaxVal = z5MinVal - 1;
    const z3MinVal = Math.floor(mhr * 0.70);
    const z3MaxVal = z4MinVal - 1;
    const z2MinVal = Math.floor(mhr * 0.60);
    const z2MaxVal = z3MinVal - 1;
    return {
      z5Min: z5MinVal,
      z5Max: z5MaxVal,
      z4Min: z4MinVal,
      z4Max: z4MaxVal,
      z3Min: z3MinVal,
      z3Max: z3MaxVal,
      z2Min: z2MinVal,
      z2Max: z2MaxVal,
    };
  }, [mhr]);

  const zoneStats = useMemo(() => {
    const zoneSeconds = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0, 0: 0 };
    const points = hrPointsData.points;
    const durationSec = hrPointsData.durationSec;

    if (!mhr || mhr <= 0 || !points || points.length === 0) {
      return { durations: zoneSeconds, totalActiveSec: 0 };
    }

    const validReadings = points.filter((p) => p.hr_value > 0);
    if (validReadings.length === 0) {
      return { durations: zoneSeconds, totalActiveSec: 0 };
    }

    let perReadingDuration = 0;
    if (validReadings.length > 1) {
      const times = validReadings.map((p) => p.time);
      const first = Math.min(...times);
      const last = Math.max(...times);
      if (last > first) {
        perReadingDuration = (last - first) / (validReadings.length - 1);
      } else {
        perReadingDuration = durationSec > 0 ? durationSec / validReadings.length : 1;
      }
    } else {
      perReadingDuration = durationSec > 0 ? durationSec : 5;
    }

    // Match iOS truncated thresholds (Int(Double(maxHeartRate) * percent))
    const z5_min = Math.floor(mhr * 0.90);
    const z4_min = Math.floor(mhr * 0.80);
    const z3_min = Math.floor(mhr * 0.70);
    const z2_min = Math.floor(mhr * 0.60);
    const z1_min = Math.floor(mhr * 0.50);

    for (const reading of validReadings) {
      const bpm = reading.hr_value;
      let zone = 0;
      if (bpm >= z5_min) {
        zone = 5;
      } else if (bpm >= z4_min) {
        zone = 4;
      } else if (bpm >= z3_min) {
        zone = 3;
      } else if (bpm >= z2_min) {
        zone = 2;
      } else if (bpm >= z1_min) {
        zone = 1;
      } else {
        zone = 0;
      }
      zoneSeconds[zone] += perReadingDuration;
    }

    const totalActiveSec =
      zoneSeconds[0] +
      zoneSeconds[1] +
      zoneSeconds[2] +
      zoneSeconds[3] +
      zoneSeconds[4] +
      zoneSeconds[5];

    return {
      durations: zoneSeconds,
      totalActiveSec,
    };
  }, [hrPointsData, mhr]);

  // Render KoT Round matches centered layout
  const kotDisplayRows = useMemo(() => {
    const playerIdForRows = kotPlayerId || kotResolvedPlayerId;
    if (!playerIdForRows || !kotMatches.length) return [];
    const nameById = new Map();
    for (const m of kotMatches) {
      if (m.player_a?.player_id) {
        nameById.set(
          String(m.player_a.player_id),
          formatKotPlayerDisplayName(m.player_a)
        );
      }
      if (m.player_b?.player_id) {
        nameById.set(
          String(m.player_b.player_id),
          formatKotPlayerDisplayName(m.player_b)
        );
      }
    }
    const resolveName = (oppId, match) =>
      resolveOpponentNameFromMatch(match, oppId, nameById);
    return attachVideoUrlsToKotRows(
      sortKotPlayerMatchRows(
        kotMatches.map((m) => buildKotPlayerMatchRow(m, playerIdForRows, resolveName))
      ),
      buildKotMatchVideoUrlMap(
        feedbacksForKotVideo,
        kotMatches.map((m) => m.kot_match_id).filter(Boolean),
        ip_back,
        { teId: exercise?.te_id }
      ),
      kotMatches,
      ip_back,
      feedbacksForKotVideo
    );
  }, [kotMatches, kotPlayerId, kotResolvedPlayerId, feedbacksForKotVideo, exercise?.te_id]);

  const kotLeaderboardForDisplay = useMemo(() => {
    if (!isPlayerUi || viewerParticipatedInSession) return kotLeaderboard;
    const pid = kotPlayerId || kotResolvedPlayerId;
    if (!pid) return [];
    return kotLeaderboard.filter((row) => String(row.player_id) === String(pid));
  }, [
    kotLeaderboard,
    isPlayerUi,
    viewerParticipatedInSession,
    kotPlayerId,
    kotResolvedPlayerId,
  ]);

  const kotExerciseMatchesForDisplay = useMemo(() => {
    if (!isPlayerUi || viewerParticipatedInSession) return kotExerciseMatches;
    const pid = kotPlayerId || kotResolvedPlayerId;
    if (!pid) return [];
    return kotExerciseMatches.filter(
      (m) =>
        String(m.player_a_id) === String(pid) ||
        String(m.player_b_id) === String(pid)
    );
  }, [
    kotExerciseMatches,
    isPlayerUi,
    viewerParticipatedInSession,
    kotPlayerId,
    kotResolvedPlayerId,
  ]);

  /** Enriched match list for Media tab labels (round / table / players). */
  const kotMatchesForMedia = useMemo(() => {
    if (!isKot) return [];
    const byId = new Map();
    for (const m of [...kotExerciseMatches, ...kotMatches]) {
      const id = m?.kot_match_id != null ? String(m.kot_match_id) : "";
      if (!id) continue;
      const existing = byId.get(id);
      // Prefer the row that already has display names / round.
      if (
        !existing ||
        ((!existing.player_a || !existing.round_number) &&
          (m.player_a || m.round_number))
      ) {
        byId.set(id, m);
      }
    }
    return Array.from(byId.values());
  }, [isKot, kotExerciseMatches, kotMatches]);

  const exerciseHeaderSubtitle = useMemo(() => {
    const meta = {
      te_duration: fullExercise?.te_duration ?? exercise?.te_duration,
      total_duration: exercise?.total_duration,
      sequences: fullExercise?.sequences || exercise?.sequences,
      te_start_time: exercise?.te_start_time ?? fullExercise?.te_start_time,
      table_number: exercise?.table_number ?? fullExercise?.table_number,
    };
    const startTime = formatExerciseStartTime(meta.te_start_time, localeCode);
    const duration = formatExerciseDuration(meta, t);
    const showViewerPlayerName =
      !!(effectivePlayerId || effectiveUserId) || isPlayerUi;
    const playerName = showViewerPlayerName
      ? formatExercisePlayerName(
          targetPlayer,
          resolvedPlayerId || resolvedUserId
        )
      : "";
    const displayName = playerName && playerName !== "—" ? playerName : "";
    const tableLabel = formatExerciseTableLabel(
      resolveViewerTableNumber(
        {
          ...exercisePlayerContext,
          table_number: meta.table_number,
        },
        resolvedPlayerId,
        resolvedUserId
      ),
      t
    );
    return [
      t("finalized", "Finalized"),
      startTime,
      duration,
      displayName,
      tableLabel,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [
    fullExercise,
    exercise,
    localeCode,
    t,
    targetPlayer,
    resolvedPlayerId,
    resolvedUserId,
    effectivePlayerId,
    effectiveUserId,
    isPlayerUi,
    exercisePlayerContext,
  ]);

  if (!open || !exercise) return null;

  return (
    <>
    {createPortal(
    <div
      className={`player-session-results__backdrop${
        onBack ? " player-session-results__backdrop--nested" : ""
      }`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`player-session-results__sheet${
          onBack ? " player-session-results__sheet--nested" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="player-session-results__handle"></div>

        <div className="player-session-results__content">
          <div className="player-session-results__header">
            <div className="player-session-results__header-top">
              {onBack ? (
                <button
                  type="button"
                  className="player-session-results__back-btn player-session-results__back-btn--ios-nav"
                  onClick={onBack}
                >
                  <ArrowBackIcon fontSize="small" />
                  <span>{t("back", "Back")}</span>
                </button>
              ) : (
                <span
                  className="player-session-results__header-top-spacer"
                  aria-hidden="true"
                />
              )}
              <div
                className="player-session-results__header-actions player-session-results__header-actions--inline"
                role="group"
                aria-label={t("exerciseTools", "Exercise tools")}
              >
                {!isPlayerUi ? (
                  <button
                    type="button"
                    className="player-session-results__icon-btn"
                    onClick={() => setVideoAnalysisSettingsOpen(true)}
                    aria-label={t("videoAnalysisSettings", "Video analysis settings")}
                    title={t("videoAnalysisSettings", "Video analysis settings")}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="player-session-results__close"
                  onClick={onClose}
                  aria-label={t("close", "Close")}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            <div className="player-session-results__header-new">
              {exerciseNav.total > 1 ? (
                <div
                  className="player-session-results__ex-nav"
                  role="group"
                  aria-label={t("exerciseNavigation", "Exercise navigation")}
                >
                  <button
                    type="button"
                    className="player-session-results__ex-nav-btn"
                    onClick={() => goToSessionExercise(exerciseNav.previous)}
                    disabled={!exerciseNav.previous}
                    aria-label={
                      exerciseNav.previous
                        ? t("previousExercise", "Previous exercise") +
                          `: ${exerciseNavLabel(exerciseNav.previous, t, resultsLang)}`
                        : t("previousExercise", "Previous exercise")
                    }
                    title={
                      exerciseNav.previous
                        ? `#${exerciseNav.previous.te_order || "?"} · ${exerciseNavLabel(exerciseNav.previous, t, resultsLang)}`
                        : undefined
                    }
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </button>
                  <div className="player-session-results__badge-circle">
                    #{exercise?.te_order || 1}
                  </div>
                  <button
                    type="button"
                    className="player-session-results__ex-nav-btn"
                    onClick={() => goToSessionExercise(exerciseNav.next)}
                    disabled={!exerciseNav.next}
                    aria-label={
                      exerciseNav.next
                        ? t("nextExercise", "Next exercise") +
                          `: ${exerciseNavLabel(exerciseNav.next, t, resultsLang)}`
                        : t("nextExercise", "Next exercise")
                    }
                    title={
                      exerciseNav.next
                        ? `#${exerciseNav.next.te_order || "?"} · ${exerciseNavLabel(exerciseNav.next, t, resultsLang)}`
                        : undefined
                    }
                  >
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <div className="player-session-results__badge-circle">
                  #{exercise?.te_order || 1}
                </div>
              )}
              <div className="player-session-results__header-text">
                <div className="player-session-results__title-row">
                  <h2 className="player-session-results__title-new">
                    {exName}
                  </h2>
                  {!isPlayerUi && !isKot && exercise?.te_id ? (
                    <button
                      type="button"
                      className="player-session-results__icon-btn"
                      onClick={() => setChangeExerciseOpen(true)}
                      aria-label={t("changeExerciseOnResults", "Change exercise")}
                      title={t("changeExerciseOnResults", "Change exercise")}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </button>
                  ) : null}
                </div>
                <p className="player-session-results__subtitle-new">
                  {exerciseHeaderSubtitle}
                  {exerciseNav.total > 1 && exerciseNav.index >= 0 ? (
                    <span className="player-session-results__ex-nav-count">
                      {" · "}
                      {t("exerciseOfTotal", "{{current}} of {{total}}", {
                        current: exerciseNav.index + 1,
                        total: exerciseNav.total,
                      })}
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>

          <div className="player-session-results__body">
            <TePipelineDebugPanel
              tsId={session?.ts_id || session?.id || session?.session_id}
              focusTeId={exercise?.te_id}
              defaultExpanded
            />
            {showSetCatalogExercise ? (
              <div className="player-exercise-details__catalog-missing" role="note">
                <p className="player-exercise-details__catalog-missing-text">
                  {t(
                    "exerciseNotSetOnResultsHint",
                    "No catalogue exercise was set for this block. Choose the exercise that was performed."
                  )}
                </p>
                <button
                  type="button"
                  className="player-exercise-details__catalog-missing-btn"
                  onClick={() => setChangeExerciseOpen(true)}
                >
                  <SwapHorizIcon fontSize="small" />
                  <span>{t("setExercisePerformed", "Set exercise performed")}</span>
                </button>
              </div>
            ) : null}
            <div className="player-exercise-details__tabs">
              <button
                type="button"
                className={`player-exercise-details__tab ${
                  activeTab === "perf" ? "player-exercise-details__tab--active" : ""
                }`}
                onClick={() => setActiveTab("perf")}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="3" y="12" width="4" height="8" rx="1" />
                  <rect x="10" y="7" width="4" height="13" rx="1" />
                  <rect x="17" y="3" width="4" height="17" rx="1" />
                </svg>
                <span>{t("perf_short", "Perf")}</span>
              </button>
              {showHrTab ? (
              <button
                type="button"
                className={`player-exercise-details__tab ${
                  activeTab === "hr" ? "player-exercise-details__tab--active" : ""
                }`}
                onClick={() => setActiveTab("hr")}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                <span>{t("hr_short", "HR")}</span>
              </button>
              ) : null}
              <button
                type="button"
                className={`player-exercise-details__tab ${
                  activeTab === "feedback" ? "player-exercise-details__tab--active" : ""
                }`}
                onClick={() => setActiveTab("feedback")}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                </svg>
                <span>{t("media", "Media")}</span>
              </button>
              {!isKot ? (
              <button
                type="button"
                className={`player-exercise-details__tab ${
                  activeTab === "autoeval" ? "player-exercise-details__tab--active" : ""
                }`}
                onClick={() => setActiveTab("autoeval")}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                <span>{t("autoEvalTab", "Auto-eval")}</span>
              </button>
              ) : null}
            </div>

            {activeTab === "perf" && (
              isKot ? (
                /* --- King of Table (KoT) view --- */
                kotLoading ? (
                  <div className="player-session-results__loading">
                    <CircularProgress size={36} style={{ color: "#0A84FF" }} />
                    <p style={{ marginTop: 12 }}>{t("loading", "Loading match history...")}</p>
                  </div>
                ) : kotError ? (
                  <div className="player-session-results__empty">
                    <p style={{ color: "#FF453A" }}>{kotError}</p>
                  </div>
                ) : kotDisplayRows.length === 0 && !kotLeaderboardForDisplay.length ? (
                  <div className="player-session-results__empty">
                    <FitnessCenterIcon className="player-session-results__empty-icon" />
                    <p>{t("kotPlayerMatchHistoryEmpty", "No matches recorded for this exercise.")}</p>
                  </div>
                ) : (
                  <KotPlayerMatchList
                    rows={kotDisplayRows}
                    leaderboard={kotLeaderboardForDisplay}
                    exerciseMatches={kotExerciseMatchesForDisplay}
                    highlightPlayerId={kotPlayerId || kotResolvedPlayerId}
                    onPlayVideo={handlePlayKotMatchVideo}
                  />
                )
              ) : (
                /* --- Standard sequences / single-timer view --- */
                <div className="player-exercise-details__perf-sequences">
                  <ExerciseToolResults
                    teId={exercise?.te_id || fullExercise?.te_id || null}
                    userId={resolvedUserId}
                    showEmpty={isOffTable}
                    refreshKey={refreshCount}
                    t={t}
                  />
                  {isOffTable ? null : loading ? (
                    <div className="player-session-results__loading">
                      <CircularProgress size={28} />
                    </div>
                  ) : isOnceExercise ? (
                    teOncePerformanceEntry ? (
                      <>
                        <h3 className="player-exercise-details__perf-list-heading">
                          {t("perfSequencesPerformed", "Sequences performed")}
                        </h3>
                        <PlayerPerformedSequencesList
                          sequences={[teOncePerformanceEntry]}
                          localeCode={localeCode}
                          t={t}
                          onPlayVideo={handlePlaySequenceVideo}
                          onOpenHr={handleOpenSequenceHr}
                        />
                      </>
                    ) : (
                      <div className="player-session-results__empty">
                        <p>{t("noSequencesFound", "No sequences found.")}</p>
                      </div>
                    )
                  ) : sequences.length === 0 ? (
                    <div className="player-session-results__empty">
                      <p>{t("noSequencesFound", "No sequences found.")}</p>
                    </div>
                  ) : playerPerformedSequences.length === 0 ? (
                    <div className="player-session-results__empty">
                      <p>
                        {t(
                          "noSequencesForPlayerRole",
                          "No sequences found for your active or sparring roles."
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      <h3 className="player-exercise-details__perf-list-heading">
                        {t("perfSequencesPerformed", "Sequences performed")}
                      </h3>
                      <PlayerPerformedSequencesList
                        sequences={playerPerformedSequences}
                        localeCode={localeCode}
                        t={t}
                        onPlayVideo={handlePlaySequenceVideo}
                        onOpenHr={handleOpenSequenceHr}
                      />
                    </>
                  )}
                </div>
              )
            )}

            {activeTab === "hr" && showHrTab && (
              <div className="player-exercise-details__hr-section">
                <h3 className="player-exercise-details__section-title">
                  {t("hr_training_summary", "Heart Rate Training Summary")}
                </h3>
                <div className="player-exercise-details__hr-dashboard">
                  <div className="player-exercise-details__hr-dashboard-metrics">
                    <ExerciseCardHrSummary
                      hrStats={hrStats}
                      t={t}
                      mhr={mhr}
                      metrics="intensity"
                    />

                    <ExerciseCardDefaultMetrics
                      metrics={exerciseDefaultMetrics}
                      t={t}
                    />
                  </div>

                  <div className="player-exercise-details__hr-dashboard-chart">
                    <HrPerformanceCurveChart
                      hrInfo={hrStats}
                      mhr={mhr}
                      showRoleToggle
                      highlightRole={hrHighlightRole}
                      onHighlightRoleChange={handleHrHighlightRoleChange}
                      selectedSequence={selectedHrSequence}
                      onClearSelectedSequence={handleClearSelectedHrSequence}
                      localeCode={localeCode}
                      performedSequences={playerPerformedSequences}
                      knownRoles={(playerPerformedSequences || [])
                        .map((e) => e.roleVariant || e.role)
                        .filter(Boolean)}
                    />
                  </div>

                  <div className="player-exercise-details__hr-dashboard-zones">
                    <div className="player-exercise-details__hr-zones-card">
                      <TrainingZonesInfoPanel
                        mhr={mhr}
                        age={playerDemographics?.age}
                        gender={playerDemographics?.gender}
                      />
                      <p className="session-summary-dialog__mhr-caption">
                        {t("session_training_summary_mhr_of", "of {{mhr}} bpm MHR", {
                          mhr,
                        })}
                      </p>
                      
                      <div className="player-exercise-details__zone-list">
                        <div className="player-exercise-details__zone-row">
                          <div className="player-exercise-details__zone-header">
                            <div className="player-exercise-details__zone-badge player-exercise-details__zone-badge--z5">
                              Z5
                            </div>
                            <div className="player-exercise-details__zone-info">
                              <span className="player-exercise-details__zone-name">{t("zone_peak_bpm", "Peak ({{min}}–{{max}} bpm)", { min: z5Min, max: z5Max })}</span>
                            </div>
                            <div className="player-exercise-details__zone-duration">{formatZoneDuration(zoneStats.durations[5])}</div>
                          </div>
                          <div className="player-exercise-details__zone-progress-wrap">
                            <div className="player-exercise-details__zone-progress-bar player-exercise-details__zone-progress-bar--z5" style={{ width: `${zoneStats.totalActiveSec > 0 ? (zoneStats.durations[5] / zoneStats.totalActiveSec) * 100 : 0}%` }}></div>
                          </div>
                        </div>

                        <div className="player-exercise-details__zone-row">
                          <div className="player-exercise-details__zone-header">
                            <div className="player-exercise-details__zone-badge player-exercise-details__zone-badge--z4">
                              Z4
                            </div>
                            <div className="player-exercise-details__zone-info">
                              <span className="player-exercise-details__zone-name">{t("zone_cardio_bpm", "Cardio ({{min}}–{{max}} bpm)", { min: z4Min, max: z4Max })}</span>
                            </div>
                            <div className="player-exercise-details__zone-duration">{formatZoneDuration(zoneStats.durations[4])}</div>
                          </div>
                          <div className="player-exercise-details__zone-progress-wrap">
                            <div className="player-exercise-details__zone-progress-bar player-exercise-details__zone-progress-bar--z4" style={{ width: `${zoneStats.totalActiveSec > 0 ? (zoneStats.durations[4] / zoneStats.totalActiveSec) * 100 : 0}%` }}></div>
                          </div>
                        </div>

                        <div className="player-exercise-details__zone-row">
                          <div className="player-exercise-details__zone-header">
                            <div className="player-exercise-details__zone-badge player-exercise-details__zone-badge--z3">
                              Z3
                            </div>
                            <div className="player-exercise-details__zone-info">
                              <span className="player-exercise-details__zone-name">{t("zone_aerobic_bpm", "Aerobic ({{min}}–{{max}} bpm)", { min: z3Min, max: z3Max })}</span>
                            </div>
                            <div className="player-exercise-details__zone-duration">{formatZoneDuration(zoneStats.durations[3])}</div>
                          </div>
                          <div className="player-exercise-details__zone-progress-wrap">
                            <div className="player-exercise-details__zone-progress-bar player-exercise-details__zone-progress-bar--z3" style={{ width: `${zoneStats.totalActiveSec > 0 ? (zoneStats.durations[3] / zoneStats.totalActiveSec) * 100 : 0}%` }}></div>
                          </div>
                        </div>

                        <div className="player-exercise-details__zone-row">
                          <div className="player-exercise-details__zone-header">
                            <div className="player-exercise-details__zone-badge player-exercise-details__zone-badge--z2">
                              Z2
                            </div>
                            <div className="player-exercise-details__zone-info">
                              <span className="player-exercise-details__zone-name">{t("zone_fatburn_bpm", "Fat Burn ({{min}}–{{max}} bpm)", { min: z2Min, max: z2Max })}</span>
                            </div>
                            <div className="player-exercise-details__zone-duration">{formatZoneDuration(zoneStats.durations[2])}</div>
                          </div>
                          <div className="player-exercise-details__zone-progress-wrap">
                            <div className="player-exercise-details__zone-progress-bar player-exercise-details__zone-progress-bar--z2" style={{ width: `${zoneStats.totalActiveSec > 0 ? (zoneStats.durations[2] / zoneStats.totalActiveSec) * 100 : 0}%` }}></div>
                          </div>
                        </div>

                        <div className="player-exercise-details__zone-row">
                          <div className="player-exercise-details__zone-header">
                            <div className="player-exercise-details__zone-badge player-exercise-details__zone-badge--z01">
                              Z0/Z1
                            </div>
                            <div className="player-exercise-details__zone-info">
                              <span className="player-exercise-details__zone-name">{t("zone_warmup_inactive_bpm", "Warm Up / Inactive (<{{min}} bpm)", { min: z2Min })}</span>
                            </div>
                            <div className="player-exercise-details__zone-duration">{formatZoneDuration(zoneStats.durations[1] + zoneStats.durations[0])}</div>
                          </div>
                          <div className="player-exercise-details__zone-progress-wrap">
                            <div className="player-exercise-details__zone-progress-bar player-exercise-details__zone-progress-bar--z01" style={{ width: `${zoneStats.totalActiveSec > 0 ? ((zoneStats.durations[1] + zoneStats.durations[0]) / zoneStats.totalActiveSec) * 100 : 0}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "feedback" && (
              <div className="player-exercise-details__feedback-section">
                {isPlayerUi ? (
                  <>
                    {!playerHasActiveOrSparringRole ? (
                      <div className="player-session-results__empty-box">
                        <p>
                          {t(
                            "evaluationNotAvailableForRole",
                            "Video evaluation is only available when you participated as Active or Sparring on this exercise."
                          )}
                        </p>
                      </div>
                    ) : feedbacksLoading ? (
                      <div className="player-session-results__loading">
                        <CircularProgress size={24} style={{ color: "#0A84FF" }} />
                      </div>
                    ) : playerEvaluationVideos.length === 0 ? (
                      <div className="player-session-results__empty-box">
                        <p>
                          {t(
                            "noEvaluationVideoForExercise",
                            "No evaluation video recorded for you on this exercise yet."
                          )}
                        </p>
                      </div>
                    ) : (
                      <>
                        <EvaluationVideoCoverageBanner
                          coverage={playerEvaluationVideoCoverage}
                          videoCoverageSummary={videoCoverageSummary}
                          displayedEvaluationCount={playerEvaluationVideos.length}
                          t={t}
                        />
                        {playerEvaluationVideoCoverage?.expectedCount > 0 ? (
                          <SequenceEvaluationMediaList
                            coverage={playerEvaluationVideoCoverage}
                            exercise={exercise}
                            fullExercise={fullExercise}
                            exName={exName}
                            sequences={sequences}
                            viewerIdentityIds={viewerIdentityIds}
                            localeCode={localeCode}
                            t={t}
                            openReviewVideo={openReviewVideo}
                            activePlaybackMessageId={lastPlayedMessageId}
                            kotMatches={kotMatchesForMedia}
                            canShareReel
                            onShareReel={openReelShare}
                          />
                        ) : (
                          <div className="player-exercise-details__feedback-list">
                            {playerEvaluationVideos.map((msg, index) => {
                              const absoluteUrl = toAbsoluteMediaUrl(msg.video_message_url, ip_back);
                              const formattedDate = formatFeedbackMessageDate(msg, localeCode);
                              const videoMeta = buildEvaluationVideoMetadata({
                                msg,
                                exercise,
                                fullExercise,
                                exName,
                                sequences,
                                viewerIdentityIds,
                                localeCode,
                                t,
                                videoIndex: index,
                                kotMatches: kotMatchesForMedia,
                              });

                              return (
                                <EvaluationVideoMediaCard
                                  key={msg.message_id || index}
                                  msg={msg}
                                  meta={videoMeta}
                                  formattedDate={formattedDate}
                                  videoUrl={absoluteUrl}
                                  playLabel={t("watchEvaluationVideo", "Watch evaluation video")}
                                  onPlay={openReviewVideo}
                                  canShareReel
                                  onShareReel={openReelShare}
                                  isActivePlayback={
                                    lastPlayedMessageId != null &&
                                    String(msg.message_id) === String(lastPlayedMessageId)
                                  }
                                  t={t}
                                />
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                {renderRequestVideoPanel()}
                {moveStatus && (
                  <div
                    className={`video-request-panel__status ${
                      moveStatus.success
                        ? "video-request-panel__status--success"
                        : "video-request-panel__status--error"
                    }`}
                    style={{ marginBottom: 12 }}
                  >
                    {moveStatus.message}
                  </div>
                )}
                <EvaluationVideoCoverageBanner
                  coverage={evaluationVideoCoverage}
                  videoCoverageSummary={videoCoverageSummary}
                  displayedEvaluationCount={coachEvaluationVideos.length}
                  t={t}
                />
                {evaluationUploadNotice ? (
                  <div
                    className="video-request-panel__status video-request-panel__status--success"
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {evaluationUploadNotice.message}
                    </span>
                    {evaluationUploadNotice.url ? (
                      <button
                        type="button"
                        className="video-request-panel__btn"
                        onClick={() => {
                          openReviewVideo(
                            evaluationUploadNotice.url,
                            evaluationUploadNotice.title,
                            evaluationUploadNotice.messageId
                          );
                          setEvaluationUploadNotice(null);
                        }}
                      >
                        {t("watchVideo", "Watch video")}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="video-request-panel__btn"
                      onClick={() => setEvaluationUploadNotice(null)}
                      aria-label={t("dismiss", "Dismiss")}
                    >
                      {t("dismiss", "Dismiss")}
                    </button>
                  </div>
                ) : null}
                {!isPlayerUi && orientedEvaluationCount > 0 ? (
                  <div className="video-request-panel" style={{ marginBottom: 12 }}>
                    <button
                      type="button"
                      className="video-request-panel__btn"
                      disabled={bakeOrientBusy || feedbacksLoading}
                      onClick={bakeExerciseVideoOrients}
                    >
                      {bakeOrientBusy
                        ? t("bakeOrientsWorking", "Re-encoding…")
                        : t(
                            "bakeOrientsExercise",
                            "Re-encode oriented videos ({{count}})",
                            { count: orientedEvaluationCount }
                          )}
                    </button>
                    <p className="video-request-panel__hint" style={{ marginTop: 6 }}>
                      {t(
                        "bakeOrientsExerciseHint",
                        "Writes rotation/flip into the file and replaces the source URL. Use RE on a clip to bake only that video."
                      )}
                    </p>
                    {bakeOrientStatus ? (
                      <div
                        className={`video-request-panel__status ${
                          bakeOrientStatus.success
                            ? "video-request-panel__status--success"
                            : "video-request-panel__status--error"
                        }`}
                        style={{ marginTop: 8 }}
                      >
                        {bakeOrientStatus.message}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {feedbacksLoading ? (
                  <div className="player-session-results__loading">
                    <CircularProgress size={24} style={{ color: "#0A84FF" }} />
                  </div>
                ) : coachMediaVideos.length === 0 ? (
                  <div className="player-session-results__empty-box">
                    <p>
                      {t("noFeedbackVideosYet", "No coach feedback recorded for this exercise.")}
                    </p>
                  </div>
                ) : (
                  <div className="player-exercise-details__feedback-list">
                    {evaluationVideoCoverage?.expectedCount > 0 &&
                      evaluationVideoCoverage.foundCount > 0 && (
                      <>
                        <div className="pose3d-media-toolbar">
                          <strong>{t("pose3d", "Pose3D")}</strong>
                          <span>
                            {t(
                              "pose3dSelectCount",
                              "{{n}} / 2 videos selected",
                              { n: pose3dSelection.length }
                            )}
                          </span>
                          <button
                            type="button"
                            className="pose3d-media-toolbar__btn"
                            disabled={pose3dSelection.length !== 2}
                            onClick={() => setPose3dSetupOpen(true)}
                          >
                            {t("pose3dContinue", "Continue Pose3D")}
                          </button>
                          <button
                            type="button"
                            className="pose3d-media-toolbar__btn pose3d-media-toolbar__btn--secondary"
                            disabled={!lastPose3dResult && !lastPose3dJobId}
                            onClick={() => setPose3dViewerOpen(true)}
                          >
                            {t("pose3dReplay", "Replay 3D")}
                          </button>
                          {pose3dSelection.length ? (
                            <button
                              type="button"
                              className="pose3d-media-toolbar__btn pose3d-media-toolbar__btn--secondary"
                              onClick={() => setPose3dSelection([])}
                            >
                              {t("clear", "Clear")}
                            </button>
                          ) : null}
                        </div>
                        <SequenceEvaluationMediaList
                          coverage={evaluationVideoCoverage}
                          exercise={exercise}
                          fullExercise={fullExercise}
                          exName={exName}
                          sequences={sequences}
                          viewerIdentityIds={viewerIdentityIds}
                          localeCode={localeCode}
                          t={t}
                          openReviewVideo={openReviewVideo}
                          canMoveVideo
                          onMoveVideo={setMoveVideoTarget}
                          canBenchmark
                          onBenchmark={(msg, url) =>
                            setBenchmarkTarget({
                              msg,
                              videoUrl: url || resolveMediaVideoUrl(msg?.video_message_url),
                            })
                          }
                          canBakeOrient
                          bakeOrientBusy={bakeOrientBusy}
                          bakingMessageId={bakingMessageId}
                          onBakeOrient={bakeSingleEvaluationVideo}
                          activePlaybackMessageId={lastPlayedMessageId}
                          kotMatches={kotMatchesForMedia}
                          pose3dSelectEnabled
                          pose3dSelectedIds={pose3dSelectedIds}
                          onPose3dSelect={togglePose3dSelect}
                          canShareReel
                          onShareReel={openReelShare}
                        />
                        {coachClipsMissingFromCoverage.length > 0 ? (
                          <div className="player-exercise-details__seq-video-group">
                            <h4 className="player-exercise-details__seq-video-group-title">
                              {t(
                                "otherExerciseClips",
                                "Other clips on this exercise"
                              )}
                            </h4>
                            {coachClipsMissingFromCoverage.map((msg, index) => {
                              const formattedDate = formatFeedbackMessageDate(
                                msg,
                                localeCode
                              );
                              const evaluationMeta = buildEvaluationVideoMetadata({
                                msg,
                                exercise,
                                fullExercise,
                                exName,
                                sequences,
                                viewerIdentityIds,
                                localeCode,
                                t,
                                videoIndex:
                                  coachEvaluationVideoIndexByMessageId.get(
                                    msg.message_id || `eval-${index}`
                                  ) ?? null,
                                kotMatches: kotMatchesForMedia,
                              });
                              return (
                                <EvaluationVideoMediaCard
                                  key={msg.message_id || `other-${index}`}
                                  msg={msg}
                                  meta={evaluationMeta}
                                  formattedDate={formattedDate}
                                  videoUrl={resolveMediaVideoUrl(msg.video_message_url)}
                                  playLabel={t("watchVideo", "Watch video")}
                                  onPlay={openReviewVideo}
                                  canMoveVideo
                                  onMoveVideo={setMoveVideoTarget}
                                  canBenchmark
                                  onBenchmark={(m, url) =>
                                    setBenchmarkTarget({
                                      msg: m,
                                      videoUrl:
                                        url ||
                                        resolveMediaVideoUrl(m?.video_message_url),
                                    })
                                  }
                                  canBakeOrient
                                  bakeOrientBusy={bakeOrientBusy}
                                  bakingMessageId={bakingMessageId}
                                  onBakeOrient={bakeSingleEvaluationVideo}
                                  isActivePlayback={
                                    lastPlayedMessageId != null &&
                                    String(msg.message_id) ===
                                      String(lastPlayedMessageId)
                                  }
                                  pose3dSelectEnabled
                                  pose3dSelectIndex={pose3dSelectedIds.findIndex(
                                    (id) => String(id) === String(msg.message_id)
                                  )}
                                  onPose3dSelect={togglePose3dSelect}
                                  canShareReel
                                  onShareReel={openReelShare}
                                  t={t}
                                />
                              );
                            })}
                          </div>
                        ) : null}
                      </>
                    )}
                    {coachEvaluationVideos.length > 0 &&
                      (!evaluationVideoCoverage?.expectedCount ||
                        evaluationVideoCoverage.foundCount === 0) && (
                        <>
                          <div className="pose3d-media-toolbar">
                            <strong>{t("pose3d", "Pose3D")}</strong>
                            <span>
                              {t(
                                "pose3dSelectCount",
                                "{{n}} / 2 videos selected",
                                { n: pose3dSelection.length }
                              )}
                            </span>
                            <button
                              type="button"
                              className="pose3d-media-toolbar__btn"
                              disabled={pose3dSelection.length !== 2}
                              onClick={() => setPose3dSetupOpen(true)}
                            >
                              {t("pose3dContinue", "Continue Pose3D")}
                            </button>
                            <button
                              type="button"
                              className="pose3d-media-toolbar__btn pose3d-media-toolbar__btn--secondary"
                              disabled={!lastPose3dResult && !lastPose3dJobId}
                              onClick={() => setPose3dViewerOpen(true)}
                            >
                              {t("pose3dReplay", "Replay 3D")}
                            </button>
                            {pose3dSelection.length ? (
                              <button
                                type="button"
                                className="pose3d-media-toolbar__btn pose3d-media-toolbar__btn--secondary"
                                onClick={() => setPose3dSelection([])}
                              >
                                {t("clear", "Clear")}
                              </button>
                            ) : null}
                          </div>
                          {coachEvaluationVideos.map((msg, index) => {
                            const formattedDate = formatFeedbackMessageDate(msg, localeCode);
                            const evaluationMeta = buildEvaluationVideoMetadata({
                              msg,
                              exercise,
                              fullExercise,
                              exName,
                              sequences,
                              viewerIdentityIds,
                              localeCode,
                              t,
                              videoIndex:
                                coachEvaluationVideoIndexByMessageId.get(
                                  msg.message_id || `eval-${index}`
                                ) ?? null,
                              kotMatches: kotMatchesForMedia,
                            });

                            return (
                              <EvaluationVideoMediaCard
                                key={msg.message_id || index}
                                msg={msg}
                                meta={evaluationMeta}
                                formattedDate={formattedDate}
                                videoUrl={resolveMediaVideoUrl(msg.video_message_url)}
                                playLabel={t("watchVideo", "Watch video")}
                                onPlay={openReviewVideo}
                                canMoveVideo
                                onMoveVideo={setMoveVideoTarget}
                                canBenchmark
                                onBenchmark={(m, url) =>
                                  setBenchmarkTarget({
                                    msg: m,
                                    videoUrl: url || resolveMediaVideoUrl(m?.video_message_url),
                                  })
                                }
                                canBakeOrient
                                bakeOrientBusy={bakeOrientBusy}
                                bakingMessageId={bakingMessageId}
                                onBakeOrient={bakeSingleEvaluationVideo}
                                isActivePlayback={
                                  lastPlayedMessageId != null &&
                                  String(msg.message_id) === String(lastPlayedMessageId)
                                }
                                pose3dSelectEnabled
                                pose3dSelectIndex={pose3dSelectedIds.findIndex(
                                  (id) => String(id) === String(msg.message_id)
                                )}
                                onPose3dSelect={togglePose3dSelect}
                                canShareReel
                                onShareReel={openReelShare}
                                t={t}
                              />
                            );
                          })}
                        </>
                      )}
                    {coachNonEvaluationMedia.map((msg, index) => {
                      const isEvaluation = false;
                      const isVideo = msg.message_type === "video" || (!msg.message_type && msg.video_message_url);
                      const isVoice = msg.message_type === "voice" || msg.message_type === "audio" || (!msg.message_type && msg.voice_message_url);
                      const isText = msg.message_type === "text" || (isEvaluation && !msg.video_message_url) || (!msg.message_type && msg.text_message && !msg.video_message_url && !msg.voice_message_url);

                      const dateObj = msg.createdAt ? new Date(msg.createdAt) : null;
                      const formattedDate = dateObj 
                        ? `${dateObj.toLocaleDateString(localeCode === "fr" ? "fr-FR" : "en-US", { month: "short", day: "numeric" })} @ ${dateObj.toLocaleTimeString(localeCode === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })}`
                        : "";

                      const senderName = msg.sender_first_name 
                        ? `${msg.sender_first_name} ${msg.sender_last_name || ""}`.trim()
                        : isEvaluation
                          ? t("evaluationClip", "Evaluation Video")
                          : t("coach", "Coach");
                      const senderInitials = msg.sender_first_name 
                        ? `${msg.sender_first_name.charAt(0)}${msg.sender_last_name ? msg.sender_last_name.charAt(0) : ""}`
                        : isEvaluation ? "EV" : "CH";

                      const roleLabel = isEvaluation
                        ? t("systemAuto", "System Recording")
                        : t("coach", "Coach");

                      const evaluationMeta = isEvaluation
                        ? buildEvaluationVideoMetadata({
                            msg,
                            exercise,
                            fullExercise,
                            exName,
                            sequences,
                            viewerIdentityIds,
                            localeCode,
                            t,
                            videoIndex:
                              coachEvaluationVideoIndexByMessageId.get(
                                msg.message_id || `eval-${index}`
                              ) ?? null,
                            kotMatches: kotMatchesForMedia,
                          })
                        : null;

                      return (
                        <div key={msg.message_id || index} className="player-exercise-details__feedback-card">
                          <div className="player-exercise-details__feedback-meta">
                            <div className="player-exercise-details__feedback-sender">
                              {msg.sender_image ? (
                                <img 
                                  src={msg.sender_image} 
                                  alt={senderName} 
                                  className="player-exercise-details__sender-img"
                                />
                              ) : (
                                <div className="player-exercise-details__sender-avatar-placeholder">
                                  {senderInitials}
                                </div>
                              )}
                              <div className="player-exercise-details__sender-info">
                                <span className="player-exercise-details__sender-name">{senderName}</span>
                                <span className="player-exercise-details__sender-role">
                                  {roleLabel}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                              {isVideo && (
                                <span className={`player-exercise-details__feedback-badge ${isEvaluation ? "player-exercise-details__feedback-badge--evaluation" : "player-exercise-details__feedback-badge--video"}`}>
                                  {isEvaluation ? <MovieIcon style={{ fontSize: 13, marginRight: 2 }} /> : <VideocamIcon style={{ fontSize: 13, marginRight: 2 }} />}
                                  {isEvaluation ? t("evaluation", "Evaluation") : t("videoFeedback", "Video")}
                                </span>
                              )}
                              {isVoice && (
                                <span className="player-exercise-details__feedback-badge player-exercise-details__feedback-badge--voice">
                                  <RecordVoiceOverIcon style={{ fontSize: 13, marginRight: 2 }} />
                                  {t("voiceFeedback", "Voice")}
                                </span>
                              )}
                              {isText && !isEvaluation && (
                                <span className="player-exercise-details__feedback-badge player-exercise-details__feedback-badge--text">
                                  <MessageIcon style={{ fontSize: 13, marginRight: 2 }} />
                                  {t("textFeedback", "Comment")}
                                </span>
                              )}
                              {isText && isEvaluation && (
                                <span className="player-exercise-details__feedback-badge player-exercise-details__feedback-badge--evaluation">
                                  <MovieIcon style={{ fontSize: 13, marginRight: 2 }} />
                                  {t("evaluation", "Evaluation")}
                                </span>
                              )}
                              {formattedDate && (
                                <span className="player-exercise-details__feedback-date">{formattedDate}</span>
                              )}
                            </div>
                          </div>

                          <div className="player-exercise-details__feedback-content">
                            {isVideo && msg.video_message_url && (
                              <MediaVideoPlayRow
                                label={
                                  isEvaluation && evaluationMeta?.title
                                    ? evaluationMeta.title
                                    : undefined
                                }
                                description={
                                  isEvaluation
                                    ? formatEvaluationVideoDescription(msg, t)
                                    : msg.text_message ||
                                      (isEvaluation
                                        ? t("evaluationClip", "Evaluation Video")
                                        : t("videoFeedback", "Video feedback"))
                                }
                                videoUrl={resolveMediaVideoUrl(msg.video_message_url)}
                                playLabel={t("watchVideo", "Watch video")}
                                messageId={msg.message_id || null}
                                onPlay={openReviewVideo}
                              />
                            )}
                            {isVoice && msg.voice_message_url && (
                              <div className="player-exercise-details__media-wrapper">
                                <audio 
                                  controls 
                                  preload="metadata"
                                  src={msg.voice_message_url}
                                />
                              </div>
                            )}
                            {msg.text_message && !isVideo && (
                              <div className="player-exercise-details__text-bubble" style={{ marginTop: isVoice ? 8 : 0 }}>
                                {msg.text_message}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                  </>
                )}
              </div>
            )}

            {activeTab === "autoeval" && (
              <div className="player-exercise-details__eval-section">
                {isPlayerUi ? (
                  <>
                    {(!perfVal && !exertionVal) || isEditingEval ? (
                      <div className="player-exercise-details__eval-form-card">
                        <h3 className="player-exercise-details__feedback-title" style={{ marginBottom: 16 }}>
                          {t("selfEvaluationFormTitle", "Self-Evaluation Form")}
                        </h3>

                        <EmojiRatingRow
                          icons={PERF_ICONS}
                          labels={perfLabels}
                          colors={PERF_COLORS}
                          value={editPerf || null}
                          onChange={setEditPerf}
                          title={t("ae_performance_rate", "Rate your Performance:")}
                        />

                        <EmojiRatingRow
                          icons={EXERTION_ICONS}
                          labels={exertionLabels}
                          colors={EXERTION_COLORS}
                          value={editExertion || null}
                          onChange={setEditExertion}
                          title={t("ae_exertion_rate", "Rate your Effort:")}
                        />

                        <div className="player-exercise-details__actions-container">
                          <button
                            type="button"
                            disabled={submittingAutoeval || !editPerf || !editExertion}
                            className="player-exercise-details__submit-btn"
                            onClick={handleSubmitAutoevaluation}
                          >
                            {submittingAutoeval ? (
                              <CircularProgress size={16} style={{ color: "currentColor" }} />
                            ) : (
                              t("submitMyEvaluation", "Submit My Evaluation")
                            )}
                          </button>
                          {isEditingEval && (
                            <button
                              type="button"
                              className="player-exercise-details__cancel-btn"
                              onClick={() => {
                                setEditPerf(perfVal || 0);
                                setEditExertion(exertionVal || 0);
                                setIsEditingEval(false);
                              }}
                            >
                              {t("cancel", "Cancel")}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="player-exercise-details__readonly-card">
                        <h3 className="player-exercise-details__feedback-title" style={{ marginBottom: 16 }}>
                          {t("myEvaluationTitle", "My Auto-Evaluation")}
                        </h3>

                        <div className="player-exercise-details__emoji-eval-summary-grid">
                          <div className="player-exercise-details__emoji-eval-summary-card">
                            <span className="player-exercise-details__emoji-eval-summary-caption">
                              {t("ae_performance", "Performance")}
                            </span>
                            <span className="player-exercise-details__emoji-eval-summary-icon">
                              {PERF_ICONS[perfVal - 1]}
                            </span>
                            <span
                              className="player-exercise-details__emoji-eval-summary-label"
                              style={{ color: PERF_COLORS[perfVal - 1] }}
                            >
                              {perfLabels[perfVal - 1]}
                            </span>
                          </div>

                          <div className="player-exercise-details__emoji-eval-summary-card">
                            <span className="player-exercise-details__emoji-eval-summary-caption">
                              {t("ae_exertion", "Effort")}
                            </span>
                            <span className="player-exercise-details__emoji-eval-summary-icon">
                              {EXERTION_ICONS[exertionVal - 1]}
                            </span>
                            <span
                              className="player-exercise-details__emoji-eval-summary-label"
                              style={{ color: EXERTION_COLORS[exertionVal - 1] }}
                            >
                              {exertionLabels[exertionVal - 1]}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="player-exercise-details__edit-eval-btn"
                          style={{ marginTop: 24 }}
                          onClick={() => {
                            setEditPerf(perfVal || 0);
                            setEditExertion(exertionVal || 0);
                            setIsEditingEval(true);
                          }}
                        >
                          {t("editMyEvaluation", "Edit My Evaluation")}
                        </button>
                      </div>
                    )}

                    {/* System Recording Section */}
                    <div className="player-exercise-details__feedback-header" style={{ marginTop: 32 }}>
                      <h3 className="player-exercise-details__feedback-title">
                        {t("evaluation", "Evaluation")}
                      </h3>
                    </div>

                    {feedbacksLoading ? (
                      <div className="player-session-results__loading">
                        <CircularProgress size={24} style={{ color: "#0A84FF" }} />
                      </div>
                    ) : systemEvaluations.length === 0 ? (
                      <div className="player-session-results__empty-box">
                        <p>
                          {t("noFeedbackVideosYet", "No automated evaluation videos recorded for this exercise.")}
                        </p>
                      </div>
                    ) : (
                      <div className="player-exercise-details__feedback-list">
                        {systemEvaluations.map((msg, index) => {
                          const dateObj = msg.createdAt ? new Date(msg.createdAt) : null;
                          const formattedDate = dateObj 
                            ? `${dateObj.toLocaleDateString(localeCode === "fr" ? "fr-FR" : "en-US", { month: "short", day: "numeric" })} @ ${dateObj.toLocaleTimeString(localeCode === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })}`
                            : "";

                          const senderName = msg.sender_first_name 
                            ? `${msg.sender_first_name} ${msg.sender_last_name || ""}`.trim()
                            : t("evaluationClip", "Evaluation Video");
                          const senderInitials = msg.sender_first_name 
                            ? `${msg.sender_first_name.charAt(0)}${msg.sender_last_name ? msg.sender_last_name.charAt(0) : ""}`
                            : "EV";

                          return (
                            <div key={msg.message_id || index} className="player-exercise-details__feedback-card">
                              <div className="player-exercise-details__feedback-meta">
                                <div className="player-exercise-details__feedback-sender">
                                  {msg.sender_image ? (
                                    <img 
                                      src={msg.sender_image} 
                                      alt={senderName} 
                                      className="player-exercise-details__sender-img"
                                    />
                                  ) : (
                                    <div className="player-exercise-details__sender-avatar-placeholder">
                                      {senderInitials}
                                    </div>
                                  )}
                                  <div className="player-exercise-details__sender-info">
                                    <span className="player-exercise-details__sender-name">{senderName}</span>
                                    <span className="player-exercise-details__sender-role">
                                      {t("systemAuto", "System Recording")}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                  <span className="player-exercise-details__feedback-badge player-exercise-details__feedback-badge--evaluation">
                                    <MovieIcon style={{ fontSize: 13, marginRight: 2 }} />
                                    {t("evaluation", "Evaluation")}
                                  </span>
                                  {formattedDate && (
                                    <span className="player-exercise-details__feedback-date">{formattedDate}</span>
                                  )}
                                </div>
                              </div>

                              <div className="player-exercise-details__feedback-content">
                                {msg.video_message_url && (
                                  <div className="player-exercise-details__media-wrapper">
                                    <MobileReviewVideo url={msg.video_message_url} />
                                  </div>
                                )}
                                {msg.text_message && (
                                  <div className="player-exercise-details__text-bubble" style={{ marginTop: msg.video_message_url ? 8 : 0 }}>
                                    {msg.text_message}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  /* COACH EXPERIENCE */
                  <>
                    {/* 1. Player Auto-Evaluation Section (Read-only) */}
                    <div className="coach-player-eval-readonly">
                      <h3 className="coach-eval-section-title">{t("playerAutoEvaluation", "Player's Auto-Evaluation")}</h3>
                      {!perfVal && !exertionVal ? (
                        <div className="coach-player-eval-empty">
                          <span className="info-dot"></span>
                          <p>{t("noPlayerAutoEvaluationYet", "No player auto-evaluation submitted yet.")}</p>
                        </div>
                      ) : (
                        <div className="coach-player-eval-card">
                          <div className="coach-player-eval-metric">
                            <span className="metric-label">{t("performance", "Performance")}:</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div className="player-exercise-details__stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon
                                    key={star}
                                    className={`player-exercise-details__star ${
                                      star <= perfVal ? "player-exercise-details__star--filled" : ""
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="metric-text" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
                                {PERF_LABELS[localeCode]?.[perfVal - 1] || PERF_LABELS.en[perfVal - 1]}
                              </span>
                            </div>
                          </div>
                          <div className="coach-player-eval-metric" style={{ marginTop: 12 }}>
                            <span className="metric-label">{t("perceivedExertion", "Exertion")}:</span>
                            <span className="player-exercise-details__rpe-pill player-exercise-details__rpe-pill--active" style={{ display: "inline-block", margin: 0, padding: "3px 10px", fontSize: 12 }}>
                              {EXERTION_LABELS[localeCode]?.[exertionVal - 1] || EXERTION_LABELS.en[exertionVal - 1]}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {!isPlayerUi && effectiveUserId && exercise?.ex_id ? (
                      <div style={{ marginTop: 24 }}>
                        <CoachObjectiveMetricPicker
                          playerUserId={effectiveUserId}
                          exId={exercise.ex_id}
                          goalRole="primary"
                          mode="edit"
                          compact
                        />
                      </div>
                    ) : null}

                    {performanceEvalExercise ? (
                      <CoachExerciseEvalRoster
                        teId={exercise?.te_id}
                        players={fullExercise?.player_details}
                        highlightPlayerId={resolvedPlayerId}
                        lang={localeCode}
                      />
                    ) : null}

                    {/* 2. Coach Evaluation Form */}
                    {!performanceEvalExercise ? (
                    <form className="coach-eval-form" style={{ marginTop: 28 }} onSubmit={handleSubmitCoachEvaluation}>
                      <h3 className="coach-eval-section-title">{t("coachEvaluationHeading", "My Evaluation (Coach)")}</h3>
                      <div className="coach-eval-form__stars-container" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <span className="coach-eval-form__label" style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>
                          {t("rating", "Rating")}:
                        </span>
                        <div className="player-exercise-details__interactive-stars" style={{ display: "flex", alignItems: "center" }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className="player-exercise-details__star-btn"
                              onClick={() => setCoachRating(star)}
                            >
                              <StarIcon
                                className={`player-exercise-details__interactive-star ${
                                  star <= coachRating ? "player-exercise-details__interactive-star--filled" : ""
                                }`}
                              />
                            </button>
                          ))}
                          {coachRating > 0 && (
                            <span className="player-exercise-details__interactive-label" style={{ marginLeft: 8, fontSize: 13, fontWeight: 600 }}>
                              {coachRating}/5
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="coach-eval-form__comments-container" style={{ marginBottom: 16 }}>
                        <textarea
                          className="coach-eval-form__comments-textarea"
                          placeholder={t("coachEvaluationCommentsPlaceholder", "Enter custom technical or physical evaluation notes for the player...")}
                          value={coachComments}
                          onChange={(e) => setCoachComments(e.target.value)}
                          rows={3}
                          style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.2)", color: "#fff", outline: "none", fontSize: 13, resize: "none" }}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submittingCoachEval || !coachRating}
                        className="coach-eval-form__submit-btn"
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 16px", borderRadius: 6, background: "#fbbf24", color: "#000", fontWeight: 700, border: "none", fontSize: 13, cursor: "pointer", transition: "all 0.2s" }}
                      >
                        {submittingCoachEval ? (
                          <CircularProgress size={16} style={{ color: "#000" }} />
                        ) : (
                          t("saveCoachEvaluation", "Save Evaluation")
                        )}
                      </button>
                    </form>
                    ) : null}

                    {/* 3. Coach Rich Feedback Hub */}
                    <div className="coach-feedback-hub" style={{ marginTop: 32 }}>
                      <h3 className="coach-eval-section-title">{t("richFeedbackHub", "Rich Feedback Hub")}</h3>
                      <div className="coach-feedback-hub__tabs" role="tablist" style={{ display: "flex", gap: 8, marginBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8 }}>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={feedbackTab === "text"}
                          className={`coach-feedback-hub__tab ${feedbackTab === "text" ? "coach-feedback-hub__tab--active" : ""}`}
                          onClick={() => setFeedbackTab("text")}
                        >
                          <MessageIcon style={{ fontSize: 14, marginRight: 6 }} />
                          {t("textCommentTab", "Text Comment")}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={feedbackTab === "voice"}
                          className={`coach-feedback-hub__tab ${feedbackTab === "voice" ? "coach-feedback-hub__tab--active" : ""}`}
                          onClick={() => setFeedbackTab("voice")}
                        >
                          <RecordVoiceOverIcon style={{ fontSize: 14, marginRight: 6 }} />
                          {t("voiceRecorderTab", "Voice Message")}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={feedbackTab === "video"}
                          className={`coach-feedback-hub__tab ${feedbackTab === "video" ? "coach-feedback-hub__tab--active" : ""}`}
                          onClick={() => setFeedbackTab("video")}
                        >
                          <VideocamIcon style={{ fontSize: 14, marginRight: 6 }} />
                          {t("uploadVideoTab", "Upload Video")}
                        </button>
                      </div>

                      <div className="coach-feedback-hub__content-box" style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.05)", padding: 16 }}>
                        {feedbackTab === "text" && (
                          <div className="feedback-hub-pane">
                            <textarea
                              className="feedback-hub-pane__textarea"
                              placeholder={t("feedbackTextPlaceholder", "Type text comments or advice here...")}
                              value={feedbackText}
                              onChange={(e) => setFeedbackText(e.target.value)}
                              rows={3}
                            />
                          </div>
                        )}

                        {feedbackTab === "voice" && (
                          <div className="feedback-hub-pane feedback-hub-pane--voice">
                            {!audioBlob ? (
                              <div className="voice-recorder-ui">
                                {isRecording ? (
                                  <div className="voice-recorder-ui__recording-card">
                                    <div className="voice-recorder-ui__mic-indicator voice-recorder-ui__mic-indicator--pulsing"></div>
                                    <span className="voice-recorder-ui__timer">
                                      {Math.floor(recordTime / 60)}:{(recordTime % 60).toString().padStart(2, "0")}
                                    </span>
                                    <button
                                      type="button"
                                      className="voice-recorder-ui__btn voice-recorder-ui__btn--stop"
                                      onClick={stopVoiceRecording}
                                    >
                                      {t("stopRecording", "Stop Recording")}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="voice-recorder-ui__btn voice-recorder-ui__btn--start"
                                    onClick={startVoiceRecording}
                                  >
                                    <span className="mic-dot"></span>
                                    {t("startRecording", "Start Native Recording")}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="voice-recorder-ui__preview-card">
                                <h4 className="preview-card-title">{t("recordingPreview", "Audio Message Preview")}</h4>
                                <audio src={audioUrl} controls className="voice-recorder-ui__audio-preview" />
                                <div className="voice-recorder-ui__preview-actions">
                                  <button
                                    type="button"
                                    className="voice-recorder-ui__btn voice-recorder-ui__btn--reset"
                                    onClick={resetVoiceRecording}
                                  >
                                    {t("reRecord", "Redo Recording")}
                                  </button>
                                </div>
                                <textarea
                                  className="feedback-hub-pane__textarea"
                                  style={{ marginTop: 12 }}
                                  placeholder={t("feedbackAudioCaptionPlaceholder", "Add an optional text caption to accompany this voice recording...")}
                                  value={feedbackText}
                                  onChange={(e) => setFeedbackText(e.target.value)}
                                  rows={2}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {feedbackTab === "video" && (
                          <div className="feedback-hub-pane feedback-hub-pane--video">
                            {!videoFile ? (
                              <div className="video-uploader-dropzone">
                                <label className="video-uploader-dropzone__label">
                                  <input
                                    type="file"
                                    accept="video/*"
                                    onChange={handleVideoFileChange}
                                    style={{ display: "none" }}
                                  />
                                  <div className="video-uploader-dropzone__inner">
                                    <VideocamIcon className="video-uploader-dropzone__icon" style={{ fontSize: 32 }} />
                                    <span className="video-uploader-dropzone__text-main">
                                      {t("chooseVideoFile", "Select Coach Feedback Video")}
                                    </span>
                                    <span className="video-uploader-dropzone__text-sub">
                                      {t("videoFileSupportText", "Supports mp4, mov, or avi files")}
                                    </span>
                                  </div>
                                </label>
                              </div>
                            ) : (
                              <div className="video-uploader-preview-card">
                                <h4 className="preview-card-title">{t("videoSelected", "Video File Selected")}</h4>
                                <div className="video-selected-details">
                                  <MovieIcon style={{ fontSize: 16, color: "#0A84FF" }} />
                                  <span className="video-selected-filename">{videoFile.name}</span>
                                  <span className="video-selected-filesize">({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                </div>
                                <div className="video-uploader-preview-actions">
                                  <button
                                    type="button"
                                    className="voice-recorder-ui__btn voice-recorder-ui__btn--reset"
                                    onClick={resetVideoFile}
                                  >
                                    {t("removeVideo", "Choose Another Video")}
                                  </button>
                                </div>
                                <textarea
                                  className="feedback-hub-pane__textarea"
                                  style={{ marginTop: 12 }}
                                  placeholder={t("feedbackVideoCaptionPlaceholder", "Add an optional text caption to accompany this feedback video...")}
                                  value={feedbackText}
                                  onChange={(e) => setFeedbackText(e.target.value)}
                                  rows={2}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* General submit button for feedback hub tab panel */}
                        <div className="feedback-hub-submit-container" style={{ marginTop: 16 }}>
                          <button
                            type="button"
                            disabled={
                              submittingFeedback ||
                              (feedbackTab === "text" && !feedbackText.trim()) ||
                              (feedbackTab === "voice" && !audioBlob) ||
                              (feedbackTab === "video" && !videoFile)
                            }
                            className="coach-feedback-hub__submit-btn"
                            onClick={handleSubmitFeedbackMessage}
                          >
                            {submittingFeedback ? (
                              <CircularProgress size={16} style={{ color: "currentColor" }} />
                            ) : (
                              t("uploadFeedbackMessage", "Submit Rich Feedback")
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* 4. Coach Feedback History Feed */}
                    <div className="coach-feedbacks-history" style={{ marginTop: 36 }}>
                      <h3 className="coach-eval-section-title">{t("feedbackHistory", "Feedback & Evaluation Feed")}</h3>
                      {feedbacksLoading ? (
                        <div className="player-session-results__loading">
                          <CircularProgress size={24} style={{ color: "#0A84FF" }} />
                        </div>
                      ) : coachFeedbacks.length === 0 ? (
                        <div className="player-session-results__empty-box">
                          <p>{t("noFeedbackSentYet", "No feedback logs or evaluations recorded for this player on this exercise.")}</p>
                        </div>
                      ) : (
                        <div className="player-exercise-details__feedback-list">
                          {coachFeedbacks.map((msg, index) => {
                            const dateObj = msg.createdAt ? new Date(msg.createdAt) : null;
                            const formattedDate = dateObj 
                              ? `${dateObj.toLocaleDateString(localeCode === "fr" ? "fr-FR" : "en-US", { month: "short", day: "numeric" })} @ ${dateObj.toLocaleTimeString(localeCode === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })}`
                              : "";

                            const senderName = msg.sender_first_name 
                              ? `${msg.sender_first_name} ${msg.sender_last_name || ""}`.trim()
                              : t("coach", "Coach");
                            const senderInitials = msg.sender_first_name 
                              ? `${msg.sender_first_name.charAt(0)}${msg.sender_last_name ? msg.sender_last_name.charAt(0) : ""}`
                              : "C";

                            let badgeClass = "player-exercise-details__feedback-badge--text";
                            let badgeLabel = t("text", "Text");
                            if (msg.message_type === "voice") {
                              badgeClass = "player-exercise-details__feedback-badge--voice";
                              badgeLabel = t("voice", "Voice");
                            } else if (msg.message_type === "video") {
                              badgeClass = "player-exercise-details__feedback-badge--video";
                              badgeLabel = t("video", "Video");
                            } else if (msg.message_type === "evaluation") {
                              badgeClass = "player-exercise-details__feedback-badge--evaluation";
                              badgeLabel = t("evaluation", "Evaluation");
                            }

                            return (
                              <div key={msg.message_id || index} className="player-exercise-details__feedback-card">
                                <div className="player-exercise-details__feedback-meta">
                                  <div className="player-exercise-details__feedback-sender">
                                    {msg.sender_image ? (
                                      <img 
                                        src={msg.sender_image} 
                                        alt={senderName} 
                                        className="player-exercise-details__sender-img"
                                      />
                                    ) : (
                                      <div className="player-exercise-details__sender-avatar-placeholder">
                                        {senderInitials}
                                      </div>
                                    )}
                                    <div className="player-exercise-details__sender-info">
                                      <span className="player-exercise-details__sender-name">{senderName}</span>
                                      <span className="player-exercise-details__sender-role">
                                        {msg.sender_role || t("coach", "Coach")}
                                      </span>
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                    <span className={`player-exercise-details__feedback-badge ${badgeClass}`}>
                                      {msg.message_type === "voice" && <RecordVoiceOverIcon style={{ fontSize: 13, marginRight: 2 }} />}
                                      {msg.message_type === "video" && <VideocamIcon style={{ fontSize: 13, marginRight: 2 }} />}
                                      {msg.message_type === "text" && <MessageIcon style={{ fontSize: 13, marginRight: 2 }} />}
                                      {msg.message_type === "evaluation" && <StarIcon style={{ fontSize: 13, marginRight: 2 }} />}
                                      {badgeLabel}
                                    </span>
                                    {formattedDate && (
                                      <span className="player-exercise-details__feedback-date">{formattedDate}</span>
                                    )}
                                  </div>
                                </div>

                                <div className="player-exercise-details__feedback-content">
                                  {msg.voice_message_url && (
                                    <div className="player-exercise-details__media-wrapper player-exercise-details__media-wrapper--audio" style={{ marginTop: 8 }}>
                                      <audio src={msg.voice_message_url} controls preload="metadata" />
                                    </div>
                                  )}
                                  {msg.video_message_url && (
                                    <MediaVideoPlayRow
                                      description={
                                        msg.text_message ||
                                        (msg.message_type === "evaluation"
                                          ? t("evaluationClip", "Evaluation Video")
                                          : t("videoFeedback", "Video feedback"))
                                      }
                                      videoUrl={resolveMediaVideoUrl(msg.video_message_url)}
                                      playLabel={t("watchVideo", "Watch video")}
                                      messageId={msg.message_id || null}
                                      onPlay={openReviewVideo}
                                    />
                                  )}
                                  {msg.text_message && !msg.video_message_url && (
                                    <div className="player-exercise-details__text-bubble" style={{ marginTop: msg.voice_message_url ? 8 : 0 }}>
                                      {msg.text_message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
    )}
    <ReviewVideoFullscreenPlayer
      url={reviewVideo?.url}
      title={reviewVideo?.title}
      onClose={() => {
        setReviewVideo(null);
        setReviewVideoHrSeries(null);
      }}
      canTransformVideo={reviewStaffTools}
      messageId={reviewVideo?.messageId || null}
      initialViewSetup={reviewVideo?.viewSetup ?? null}
      saveViewSetup={saveReviewVideoViewSetup}
      onViewSetupSaved={handleViewSetupSaved}
      onRequestReupload={requestReviewVideoReupload}
      hrSeries={reviewVideoHrSeries}
      sequenceTimestamp={reviewVideo?.sequenceTimestamp || 0}
      teId={exercise?.te_id || fullExercise?.te_id || null}
      sequenceOrder={
        reviewVideo?.sequence?.sequence_order ??
        reviewVideo?.feedback?.sequence_number ??
        reviewVideo?.viewSetup?.sequence_number ??
        null
      }
      activePlayerId={reviewVideoStrokeReelContext.activePlayerId}
      sequenceMeta={reviewVideoStrokeReelContext.sequenceMeta}
      reelOwners={reviewVideoStrokeReelContext.reelOwners}
      onStrokeReelUploaded={() => {
        reloadFeedbacks();
      }}
      tableId={
        exercise?.table_id ||
        fullExercise?.table_id ||
        reviewVideo?.feedback?.table_id ||
        null
      }
      enablePose={reviewStaffTools}
      poseFps={videoAnalysisSettings.poseFps}
      exerciseTypeEn={
        exercise?.type_ex?.type_name_en ||
        fullExercise?.type_ex?.type_name_en ||
        exercise?.tbl_exercise?.tbl_type_ex?.type_name_en ||
        fullExercise?.tbl_exercise?.tbl_type_ex?.type_name_en ||
        exercise?.type_details?.type_name_en ||
        fullExercise?.type_details?.type_name_en ||
        ""
      }
      exerciseSubtypeEn={
        exercise?.sub_type_ex?.sub_type_name_en ||
        fullExercise?.sub_type_ex?.sub_type_name_en ||
        exercise?.tbl_exercise?.tbl_sub_type_ex?.sub_type_name_en ||
        fullExercise?.tbl_exercise?.tbl_sub_type_ex?.sub_type_name_en ||
        exercise?.sub_type_ex_direct?.sub_type_name_en ||
        fullExercise?.sub_type_ex_direct?.sub_type_name_en ||
        exercise?.sub_type_details?.sub_type_name_en ||
        fullExercise?.sub_type_details?.sub_type_name_en ||
        // Localized fallbacks (still match "forehand" / "coup droit")
        exercise?.sub_type_ex?.sub_type_name_fr ||
        fullExercise?.sub_type_ex?.sub_type_name_fr ||
        exercise?.sub_type_details?.sub_type_name_fr ||
        ""
      }
      exerciseNameEn={
        [
          exercise?.ex_name_en,
          exercise?.ex_name,
          fullExercise?.ex_name_en,
          fullExercise?.ex_name,
          exercise?.tbl_exercise?.ex_name_en,
          fullExercise?.tbl_exercise?.ex_name_en,
          // Sequence subtype names often carry Forehand / Topspin
          ...(Array.isArray(fullExercise?.sequences)
            ? fullExercise.sequences.map(
                (s) =>
                  s?.sub_type_name_en ||
                  s?.sub_type_ex?.sub_type_name_en ||
                  s?.sub_type_name_fr ||
                  ""
              )
            : []),
          ...(Array.isArray(exercise?.sequences)
            ? exercise.sequences.map(
                (s) =>
                  s?.sub_type_name_en ||
                  s?.sub_type_ex?.sub_type_name_en ||
                  s?.sub_type_name_fr ||
                  ""
              )
            : []),
          reviewVideo?.title,
        ]
          .filter(Boolean)
          .join(" ")
      }
      isKotMatch={reviewVideoKotContext.isKotMatch}
      kotMatchId={reviewVideoKotContext.kotMatchId}
      isEvaluationVideo={reviewIsEvalVideo}
      isShareableReel={
        isMaxRallyEvaluationVideo(reviewVideo?.feedback) ||
        isStrokeSlowMoEvaluationVideo(reviewVideo?.feedback)
      }
      shareableReelKind={
        isMaxRallyEvaluationVideo(reviewVideo?.feedback)
          ? "max_rally"
          : isStrokeSlowMoEvaluationVideo(reviewVideo?.feedback)
            ? "stroke_slowmo"
            : "max_rally"
      }
      enableJointOverlay={reviewIsEvalOrKot}
    />
    {!isPlayerUi && (
      <ChangeExerciseOnResultsDialog
        open={changeExerciseOpen}
        onClose={() => setChangeExerciseOpen(false)}
        teId={exercise?.te_id}
        currentExercise={exercise}
        currentName={exName}
        defaultScope={needsCatalogExercise(exercise) ? "all" : "player"}
        allowPlayerScope
        playerId={resolvedPlayerId}
        playerName={formatExercisePlayerName(
          targetPlayer,
          resolvedPlayerId || resolvedUserId
        )}
        players={
          Array.isArray(fullExercise?.players) && fullExercise.players.length
            ? fullExercise.players
            : Array.isArray(exercise?.players)
              ? exercise.players
              : []
        }
        groupId={
          session?.gr_id ||
          session?.groupDetails?.gr_id ||
          session?.group_id ||
          null
        }
        onSuccess={handleChangeExerciseSuccess}
      />
    )}
    {!isPlayerUi && (
      <MoveEvaluationVideoDialog
        open={Boolean(moveVideoTarget)}
        onClose={() => setMoveVideoTarget(null)}
        message={moveVideoTarget}
        tsId={sessionTsId}
        currentTeId={exercise?.te_id}
        onMoved={handleMoveVideoSuccess}
        onDeleted={handleDeleteVideoSuccess}
        zIndex={15000}
      />
    )}
    {!isPlayerUi && (
      <BenchmarkVideoDialog
        open={Boolean(benchmarkTarget)}
        onClose={() => setBenchmarkTarget(null)}
        teId={exercise?.te_id}
        videoUrl={benchmarkTarget?.videoUrl}
        messageId={benchmarkTarget?.msg?.message_id}
        videoLabel={
          benchmarkTarget?.msg?.message_id
            ? `msg ${String(benchmarkTarget.msg.message_id).slice(0, 8)}…`
            : null
        }
        t={t}
      />
    )}
    {!isPlayerUi && (
      <VideoAnalysisSettingsDialog
        open={videoAnalysisSettingsOpen}
        onClose={() => setVideoAnalysisSettingsOpen(false)}
        onSaved={setVideoAnalysisSettings}
        t={t}
      />
    )}
    {!isPlayerUi && (pose3dSetupOpen || pose3dViewerOpen) ? (
      <Suspense fallback={null}>
        {pose3dSetupOpen ? (
          <Pose3DSetupDialog
            open={pose3dSetupOpen}
            onClose={() => setPose3dSetupOpen(false)}
            teId={exercise?.te_id}
            tableId={
              exercise?.table_id ||
              fullExercise?.table_id ||
              pose3dSelection[0]?.msg?.table_id ||
              null
            }
            cam0={pose3dSelection[0] || null}
            cam1={pose3dSelection[1] || null}
            t={t}
            onGenerated={({ jobId, pose3d }) => {
              setLastPose3dJobId(jobId || null);
              if (pose3d) setLastPose3dResult(pose3d);
              setPose3dSetupOpen(false);
              if (pose3d) setPose3dViewerOpen(true);
            }}
          />
        ) : null}
        {pose3dViewerOpen ? (
          <Pose3DWebViewer
            open={pose3dViewerOpen}
            onClose={() => setPose3dViewerOpen(false)}
            result={lastPose3dResult}
            jobId={lastPose3dJobId}
            teId={exercise?.te_id}
            onResultLoaded={setLastPose3dResult}
            t={t}
          />
        ) : null}
      </Suspense>
    ) : null}
    <ClipShareSheet
      open={Boolean(reelShareTarget)}
      onClose={() => setReelShareTarget(null)}
      videoUrl={reelShareTarget?.videoUrl || null}
      kind={reelShareTarget?.kind || "max_rally"}
      t={t}
    />
    </>
  );
}

export default ExerciseResultDialog;
